// @ts-nocheck
import React, { useState, useEffect, useCallback, Suspense, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { MoreVertical, Edit, LogOut } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, FullscreenDialogContent, DialogTitle, DialogDescription, DialogHeader } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ProfileHeroPhoto } from '../components/UserPhoto';
import {
  VerifyPaywall,
  VerifiedStatusDialog,
  ManageSubscriptionDialog,
  openSubscriptionStoreSettings,
  contactSubscriptionSupport,
} from '@/features/subscription/VerifyPaywall';
import { usePhotoContext } from '../contexts/PhotoContext';
import EditProfileModal from '../components/profile/EditProfileModal';
import { useCurrentUser, useUpdateCurrentUser, useUpdateCurrentUserProfile, useNetworkData } from '../contexts/NetworkDataContext';
import { APP_CONNECTION_SYNC } from '@/contexts/network/connectionApprovalSync';
import { useAuth } from '../hooks/useAuth';
import { useProfilePhoto } from '@/hooks/useProfilePhoto';
import { normalizePhotoUrls } from '@/lib/normalizePhotoUrls';
import { baseSupabase, supabase } from '@/integrations/supabase/client';
import { getDisplayNameWithOptionalAge } from '@/utils/displayNameUtils'; // V334.S18

// V150.17: Import components directly to avoid lazy loading issues
import { TrustScoreDialog } from '../components/TrustScoreDialog';
import ProfilePhotoViewer from '../components/ProfilePhotoViewer';
// V150.14: Import camera component for optimistic UI
import ProfilePhotoCapture from '../components/ProfilePhotoCapture';
// V145.4: Import photo recovery system
import PhotoRecoveryModal from '../components/PhotoRecoveryModal';
// V167.2: Import trust score service
import { trustScoreService } from '@/services/TrustScoreService';
import type { TrustScoreData } from '@/services/TrustScoreService';
import { NetworkStatsDisplay } from '../components/trust/NetworkStatsDisplay';
// V210.2: Import entitlements hook (replaces direct subscription checks)
import { useEntitlements } from '@/hooks/useEntitlements';
// V290: Import EntitlementService for cache clearing
import { EntitlementService } from '@/services/entitlement/EntitlementService';
// V291.6: Import trust score display and verification helpers (REVERTED to V291.2)
import { logTrustScoreDebug } from '@/utils/trustScoreDisplay';
import { calculateTrustScore } from '@/utils/trustScore';
import { isVerifiedUI, logVerificationState } from '@/utils/verificationHelpers';
import { repairEntitlementDrift } from '@/features/subscription/hooks/useSubscriptionProtection';
// V167: Bar-Ilan application dialog
import BarIlanApplicationDialog from '@/components/BarIlanApplicationDialog';
// V167: Import cache clearing function
import { clearBarIlanCache } from '@/utils/globalFetchDeduplicator';
// V322: Founding 100 Contest Card
import { FoundingContestCard } from '@/components/founding100/FoundingContestCard';
// V325.18: Render counter for re-render flood diagnosis (DEV only)
import { useRenderCounter } from '@/utils/useRenderCounter';
import { ProfilePageSkeleton } from '@/components/profile/ProfilePageSkeleton';
import { isBootReady, signalBootReady } from '@/utils/bootReady';
import { useTranslation } from 'react-i18next';
import { resolveDocumentDirection } from '@/i18n/languages';

// ============================================================================
// V325: CACHE & TTL UTILITIES
// ============================================================================
const ONE_HOUR_MS = 60 * 60 * 1000;

// V325: Lock set prevents React 18 strict-mode double-fire within same synchronous tick
const _ttlLocks = new Set<string>();

function shouldRunHourly(key: string, ttlMs = ONE_HOUR_MS): boolean {
  if (typeof window === 'undefined') return true;
  // Prevent double-fire within same synchronous tick (React strict mode)
  if (_ttlLocks.has(key)) return false;
  try {
    const raw = localStorage.getItem(key);
    const last = raw ? Number(raw) : 0;
    const now = Date.now();
    if (!last || now - last > ttlMs) {
      _ttlLocks.add(key);
      localStorage.setItem(key, String(now));
      // Release lock after microtask (covers strict mode double-invoke)
      queueMicrotask(() => _ttlLocks.delete(key));
      return true;
    }
    if (import.meta.env.DEV) {
      console.log('⏭️ TTL skip', { key, ageMs: now - last, ttlMs, remainingMs: ttlMs - (now - last) });
    }
    return false;
  } catch {
    return true; // localStorage blocked → always fetch
  }
}

function forceRunNow(...keys: string[]) {
  try { keys.forEach(k => localStorage.removeItem(k)); } catch { }
}

// V325: Full cache clear — call on logout to prevent stale data on re-login
export function clearProfileCaches(userId: string) {
  const scopes = ['refresh', 'userFetch', 'barilan'];
  scopes.forEach(scope => {
    try { localStorage.removeItem(`bn:profile:${scope}:${userId}`); } catch { }
  });
  try { sessionStorage.removeItem('v167_profile_stats_cache'); } catch { }
}

/** Preserve cookie consent (accepted|declined) across logout localStorage.clear() */
function clearLocalStoragePreservingCookieConsent() {
  const consent = localStorage.getItem('posthog_consent');
  const consentDate = localStorage.getItem('posthog_consent_date');
  const valid = consent === 'accepted' || consent === 'declined';
  localStorage.clear();
  if (valid) {
    localStorage.setItem('posthog_consent', consent!);
    if (consentDate) {
      localStorage.setItem('posthog_consent_date', consentDate);
    }
  }
}

// ============================================================================
// V325.2: BAR-ILAN DATA SESSION CACHE
// Persists Bar-Ilan fetch results across component remounts so the
// Founding 100 Contest Card renders instantly on cached Profile reopens.
// ============================================================================
const BAR_ILAN_CACHE_KEY = 'bn:profile:barilan:data';

function cacheBarIlanData(userId: string, userData: any, hasApplied: boolean, applicationStatus: string | null) {
  try {
    sessionStorage.setItem(BAR_ILAN_CACHE_KEY, JSON.stringify({
      userId,
      userData,
      hasApplied,
      applicationStatus,
      timestamp: Date.now()
    }));
  } catch { }
}

function getCachedBarIlanData(userId: string): { userData: any; hasApplied: boolean; applicationStatus: string | null } | null {
  try {
    const raw = sessionStorage.getItem(BAR_ILAN_CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.userId !== userId) return null;
    // Respect same 1-hour TTL
    if (Date.now() - (data.timestamp || 0) > ONE_HOUR_MS) return null;
    return { userData: data.userData, hasApplied: data.hasApplied, applicationStatus: data.applicationStatus };
  } catch {
    return null;
  }
}

interface NetworkStatsState {
  friendsCount: number;
  connectionsCount: number;
  matchesCount: number;
  trustScore: number;
  secondDegreeCount: number;
}

// V167.STATIC: Session-based static cache for profile stats
const SESSION_CACHE_KEY = 'v167_profile_stats_cache';

interface ProfileStatsCache {
  userId: string;
  stats: {
    connectionsCount: number;
    friendsCount: number;
    matchesCount: number;
    trustScore: number;
    secondDegreeCount: number;
  };
  connectionsHash: string; // Hash of connection IDs to detect changes
  timestamp: number;
}

// V167.STATIC: Generate hash from connections to detect changes
function generateConnectionsHash(connections: any[]): string {
  const sortedIds = connections
    .map(c => c.userId || c.id || '')
    .filter(Boolean)
    .sort()
    .join(',');
  return sortedIds;
}

// V167.STATIC: Get cached stats if valid
function getCachedProfileStats(
  userId: string,
  connections: any[]
): ProfileStatsCache | null {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;

  try {
    const cached = window.sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!cached) return null;

    const data: ProfileStatsCache = JSON.parse(cached);

    // Validate cache
    if (data.userId !== userId) {
      return null;
    }

    // V325: 1-hour TTL validation
    const age = Date.now() - (data.timestamp || 0);
    if (age > ONE_HOUR_MS) {
      if (import.meta.env.DEV) console.log('⏭️ Stats cache expired', { ageMs: age });
      return null;
    }

    const currentHash = generateConnectionsHash(connections);
    if (data.connectionsHash !== currentHash) {
      return null;
    }

    // Cache is valid
    return data;
  } catch (error) {
    console.error('❌ V167.STATIC: Error reading cache:', error);
    return null;
  }
}

// V167.STATIC: Save stats to cache
function setCachedProfileStats(
  userId: string,
  connections: any[],
  stats: NetworkStatsState
): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;

  try {
    const cache: ProfileStatsCache = {
      userId,
      stats: {
        connectionsCount: stats.connectionsCount,
        friendsCount: stats.friendsCount,
        matchesCount: stats.matchesCount,
        trustScore: stats.trustScore,
        secondDegreeCount: stats.secondDegreeCount
      },
      connectionsHash: generateConnectionsHash(connections),
      timestamp: Date.now()
    };
    window.sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.error('❌ V167.STATIC: Error saving cache:', error);
  }
}

// V167.STATIC: Clear profile stats cache (exported for NetworkDataContext)
export function clearProfileStatsCache(): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;

  try {
    window.sessionStorage.removeItem(SESSION_CACHE_KEY);
  } catch (error) {
    console.error('❌ V167.STATIC: Error clearing cache:', error);
  }
}


const Profile: React.FC = () => {
  useRenderCounter('ProfilePage');
  const { t, i18n } = useTranslation();
  const direction = resolveDocumentDirection(i18n.resolvedLanguage || i18n.language);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // V325: Memoized primitives — do NOT put searchParams objects in deps arrays
  const verifiedParam = useMemo(() => searchParams.get('verified') === '1', [searchParams]);
  const manageParam = useMemo(() => searchParams.get('manage'), [searchParams]);
  const editParam = useMemo(() => searchParams.get('edit') === 'true', [searchParams]);

  // V252: Check if navigating from onboarding photo step
  const fromOnboardingPhoto = (location.state as any)?.fromOnboardingPhoto === true;

  // V206.2: Use cached user data with auth user fallback
  const cachedUser = useCurrentUser();
  const { user: authUser, signOut } = useAuth();

  // V206.2: Use auth user as fallback if cached user not available
  const currentUser = cachedUser || (authUser ? {
    id: authUser.id,
    email: authUser.email || '',
    name: authUser.email?.split('@')[0] || 'User',
    photo_urls: authUser.user_metadata?.avatar_url ? [authUser.user_metadata.avatar_url] : [],
    bio: '',
    trust_score: 0,
    is_verified: false,
  } : null);

  // V163.8.5: Smart update methods to prevent duplicate queries
  const updateCurrentUser = useUpdateCurrentUser();
  const updateCurrentUserProfile = useUpdateCurrentUserProfile();

  // V240: Unified access to PhotoContext (single source of truth for photo URL caching)
  const photoContext = usePhotoContext();

  // V167.2ND: Get connections and 2nd degree from NetworkDataContext to respect V162 guard
  const { getTabConnections, secondDegreeConnections = [], secondDegreeCount = 0, refreshNetwork, tabCache } = useNetworkData();
  const isNetworkLoaded = Boolean(tabCache?.lastUpdated && tabCache.lastUpdated > 0);
  // V193.1: Get refreshProfile early to avoid TDZ error
  const { refreshProfile } = useAuth();
  // V167.2ND.FIX: Use 'all' for display, but 'first' for trust score calculation (friends count)
  const allConnections = getTabConnections ? getTabConnections('all') : [];
  const connections = getTabConnections ? getTabConnections('first') : []; // Only 1st degree for trust score calc

  // V325.3: Initialize Bar-Ilan state from session cache for instant render on remount
  const [barIlanUserData, setBarIlanUserData] = useState<any | null>(() => {
    const userId = cachedUser?.id || authUser?.id;
    if (!userId) return null;
    const cached = getCachedBarIlanData(userId);
    return cached?.userData ?? null;
  });
  const [barIlanHasApplied, setBarIlanHasApplied] = useState(() => {
    const userId = cachedUser?.id || authUser?.id;
    if (!userId) return false;
    const cached = getCachedBarIlanData(userId);
    return cached?.hasApplied ?? false;
  });
  const [barIlanApplicationStatus, setBarIlanApplicationStatus] = useState<string | null>(() => {
    const userId = cachedUser?.id || authUser?.id;
    if (!userId) return null;
    const cached = getCachedBarIlanData(userId);
    return cached?.applicationStatus ?? null;
  });
  const [barIlanIsApplying, setBarIlanIsApplying] = useState(false);
  const [showBarIlanDialog, setShowBarIlanDialog] = useState(false);
  const [barIlanFetchSettled, setBarIlanFetchSettled] = useState(() => {
    const userId = cachedUser?.id || authUser?.id;
    if (!userId) return false;
    return !!getCachedBarIlanData(userId);
  });
  const cohortSectionRef = useRef<HTMLDivElement>(null);

  // V325.4: Sync Bar-Ilan cache once userId resolves (useState initializer may miss it)
  useEffect(() => {
    const userId = currentUser?.id;
    if (!userId || barIlanUserData) return; // Already have data or no user yet

    const cached = getCachedBarIlanData(userId);
    if (cached) {
      if (import.meta.env.DEV) console.log('🚀 V325.4: Restoring Bar-Ilan from cache (post-mount sync)');
      setBarIlanUserData(cached.userData);
      setBarIlanHasApplied(cached.hasApplied);
      setBarIlanApplicationStatus(cached.applicationStatus);
      setBarIlanFetchSettled(true);
    }
  }, [currentUser?.id, barIlanUserData]);

  // V322: Bottom padding for mobile safe area (bottom nav height + spacing + safe area)
  // Bottom nav is 60px min-height + 24px spacing + safe area buffer = 88px safe default
  const bottomPadding = 88;

  // V167: Clear any cached 403 errors from fetch deduplicator on mount
  useEffect(() => {
    clearBarIlanCache();
  }, []);

  // Profile loaded - stats will be calculated separately
  useEffect(() => {
    if (import.meta.env.DEV && allConnections.length > 0) {
      console.log('🚀 V167.2ND: Profile loaded with:', {
        connections: allConnections.length,
        firstDegree: connections.length,
        secondDegree: secondDegreeConnections.length
      });
    }
  }, [allConnections.length, connections.length, secondDegreeConnections.length]);

  // Bar-Ilan: load user stats + application status using DIRECT baseSupabase client
  // V325: Throttled to once per hour using shouldRunHourly
  // V325.2: Cache results in sessionStorage so data survives component remounts
  useEffect(() => {
    const fetchBarIlanData = async () => {
      if (!currentUser?.id) return;

      const key = `bn:profile:barilan:${currentUser.id}`;
      if (!shouldRunHourly(key, ONE_HOUR_MS)) {
        if (import.meta.env.DEV) console.log('⏭️ Skipping Bar-Ilan fetch (hourly TTL)');

        // Restore from session cache (fast path)
        const cached = getCachedBarIlanData(currentUser.id);
        if (cached) {
          if (import.meta.env.DEV) console.log('🚀 V325.5: Restoring Bar-Ilan from session cache');
          setBarIlanUserData(cached.userData);
          setBarIlanHasApplied(cached.hasApplied);
          setBarIlanApplicationStatus(cached.applicationStatus);
          setBarIlanFetchSettled(true);
          return;
        }

        // V325.5 CRITICAL FIX:
        // localStorage TTL survives app restart, but sessionStorage is wiped.
        // If TTL says "skip" but session cache is empty, we MUST fetch now —
        // otherwise the Founding 100 card disappears for up to 1 hour.
        if (import.meta.env.DEV) console.warn('⚠️ V325.5: Bar-Ilan TTL skip but no session cache — forcing fetch');
        forceRunNow(key);
      }

      try {
        console.log('🔍 V322: Fetching Bar-Ilan data for user:', currentUser.id);

        // Use baseSupabase DIRECTLY - no wrappers
        const { data: { session }, error: sessionError } = await baseSupabase.auth.getSession();

        if (sessionError || !session) {
          console.error('❌ V322: No session:', sessionError);
          return;
        }

        console.log('✅ V322: Session valid');

        // Direct query to users table
        const { data: userData, error: userError } = await baseSupabase
          .from('users')
          .select('trust_score, friends_count, second_degree_count')
          .eq('id', currentUser.id)
          .single();

        if (userError) {
          console.error('❌ V322: Users query failed:', userError);
          return;
        }

        console.log('✅ V322: User data loaded:', userData);
        setBarIlanUserData((prev: any) => {
          if (prev?.trust_score === userData?.trust_score &&
              prev?.friends_count === userData?.friends_count &&
              prev?.second_degree_count === userData?.second_degree_count) {
            return prev;
          }
          return userData;
        });

        // Direct query to founding_100_applications
        const { data: appData, error: appError } = await baseSupabase
          .from('founding_100_applications')
          .select('id, status')
          .eq('user_id', currentUser.id)
          .maybeSingle();

        if (appError && appError.code !== 'PGRST116') {
          console.error('❌ V322: Application query failed:', appError);
        }

        const hasApplied = !!appData;
        const applicationStatus = appData?.status ?? null;

        console.log('✅ V322: Application status:', hasApplied ? applicationStatus : 'NOT APPLIED');
        setBarIlanHasApplied(hasApplied);
        setBarIlanApplicationStatus(applicationStatus);

        // V325.2: Cache the fetched data for future remounts
        cacheBarIlanData(currentUser.id, userData, hasApplied, applicationStatus);
        if (import.meta.env.DEV) console.log('✅ V325.2: Bar-Ilan data cached in sessionStorage');

      } catch (error) {
        console.error('❌ V322: Bar-Ilan fetch error:', error);
      } finally {
        setBarIlanFetchSettled(true);
      }
    };

    void fetchBarIlanData();
  }, [currentUser?.id]);

  const handleBarIlanApply = async () => {
    if (!currentUser?.id || !authUser?.id) {
      toast.error('Authentication error. Please refresh and try again.');
      return;
    }

    setBarIlanIsApplying(true);

    try {
      // Ultra-simple insert - only user_id needed
      const { error } = await baseSupabase
        .from('founding_100_applications')
        .insert([{
          user_id: currentUser.id,
          // status defaults to 'pending'
          // applied_at defaults to NOW()
        }]);

      if (error) {
        if (error.code === '23505') {
          toast.error('You have already applied.');
          setBarIlanHasApplied(true);
        } else {
          console.error('Application error:', error);
          toast.error('Failed to submit application. Please try again.');
        }
        return;
      }

      // Success!
      setBarIlanHasApplied(true);
      toast.success('Application submitted! We\'ll review and contact you within 48 hours.');

    } catch (err) {
      console.error('Unexpected error:', err);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setBarIlanIsApplying(false);
    }
  };

  // V167.PUSH.FIX9: Check for pending invite data on Profile load and convert if needed
  useEffect(() => {
    const checkAndConvertInviteData = async () => {
      const conversionGuardKey = 'inviteConversionInProgress';
      if (sessionStorage.getItem(conversionGuardKey) === 'true') {
        return;
      }

      // V167.PUSH.FIX9: Check BOTH localStorage AND sessionStorage
      const pendingInviteContext = localStorage.getItem('pendingInviteContext') || sessionStorage.getItem('pendingInviteContext');
      const inviteChoice = localStorage.getItem('inviteChoice') || sessionStorage.getItem('inviteChoice');
      const showPostOnboardingInvitePopup = localStorage.getItem('showPostOnboardingInvitePopup');
      const postOnboardingInviteData = localStorage.getItem('postOnboardingInviteData');

      // V167.PUSH.FIX9: FALLBACK - If we have pending invite data but no popup data, convert it now
      if (pendingInviteContext && inviteChoice === 'connect' && !postOnboardingInviteData) {
        try {
          sessionStorage.setItem(conversionGuardKey, 'true');
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const inviteData = JSON.parse(pendingInviteContext);

            localStorage.setItem('showPostOnboardingInvitePopup', 'true');
            localStorage.setItem('postOnboardingInviteData', JSON.stringify({
              ...inviteData,
              userId: user.id,
              storedAt: new Date().toISOString()
            }));
            window.dispatchEvent(new CustomEvent('invite-storage-ready'));

            // Clean up pending data from BOTH storages
            localStorage.removeItem('inviteChoice');
            localStorage.removeItem('pendingInviteContext');
            sessionStorage.removeItem('inviteChoice');
            sessionStorage.removeItem('pendingInviteContext');
          }
        } catch (error) {
          console.error('❌ V167.PUSH.FIX9: FALLBACK conversion error:', error);
        } finally {
          sessionStorage.removeItem(conversionGuardKey);
        }
      }
    };

    checkAndConvertInviteData();
  }, []);

  // V207.30: Force refresh network data on Profile load AND directly fetch user data
  // CRITICAL: Directly query user data to ensure we have fresh date_of_birth for button logic
  // This fixes the issue where cachedUser is stale on first load
  // V325: TTL guard — skip expensive refreshes if already done within 1 hour
  useEffect(() => {
    const loadProfileData = async () => {
      if (!authUser?.id) {
        setProfileDataLoaded(true);
        return;
      }

      // V325: TTL guard — skip expensive refreshes if already done within 1 hour
      const key = `bn:profile:loadProfileData:${authUser.id}`;
      const isForced = verifiedParam || fromOnboardingPhoto;
      if (isForced) forceRunNow(key);

      if (!isForced && !shouldRunHourly(key, ONE_HOUR_MS)) {
        if (import.meta.env.DEV) console.log('⏭️ V325: loadProfileData skipped (hourly TTL)');
        setProfileDataLoaded(true);
        return;
      }

      if (refreshNetwork) {
        if (import.meta.env.DEV) console.log('🔄 V207.31: Refreshing network data on Profile load');
        await refreshNetwork();
        if (import.meta.env.DEV) console.log('✅ V207.31: Network data refreshed');
      }

      // V207.31: CRITICAL FIX - Directly fetch user data from database to ensure fresh data
      // This ensures we have the latest date_of_birth for button logic, regardless of cachedUser
      // Store in local state (freshUserData) for immediate use in button logic
      try {
        if (import.meta.env.DEV) console.log('🔍 V207.31: Directly fetching user data for button logic:', authUser.id);
        // V210.2: Removed is_verified from query - use entitlements instead
        const { data: userData, error } = await supabase
          .from('users')
          .select('id, name, date_of_birth, photo_urls')
          .eq('id', authUser.id)
          .maybeSingle();

        if (error) {
          console.warn('⚠️ V207.31: Error fetching user data:', error);
          // Keep cached/fresh data visible on error — do not clear to null
        } else if (userData) {
          if (import.meta.env.DEV) console.log('✅ V207.31: User data fetched:', {
            hasName: !!userData.name,
            hasDOB: !!userData.date_of_birth,
            hasPhoto: !!userData.photo_urls && userData.photo_urls.length > 0,
          });
          // V207.31: Store fresh data in local state for immediate use in button logic
          // V210.2: Removed is_verified - use entitlements instead
          setFreshUserData({
            name: userData.name,
            date_of_birth: userData.date_of_birth,
            photo_urls: userData.photo_urls,
          });
          // V207.31: Also update cachedUser in context for other parts of the app
          // V210.2: Removed is_verified from update - use entitlements instead
          // V213.5: NEVER write photo_urls from Profile.tsx - only upload pipeline should write photo_urls
          // Profile.tsx should only update name, date_of_birth, etc. - not photo_urls
          if (updateCurrentUser) {
            await updateCurrentUser({
              name: userData.name,
              date_of_birth: userData.date_of_birth,
              // photo_urls removed - upload pipeline owns DB writes for photos
            });
            if (import.meta.env.DEV) console.log('✅ V207.31: Updated cachedUser with fresh data (photo_urls excluded - upload pipeline owns it)');
          }
        } else {
          if (import.meta.env.DEV) console.log('ℹ️ V207.31: No user data found (new user)');
          // V207.31: New user - set freshUserData to null so button shows "Start quick onboarding now"
          setFreshUserData(null);
        }
      } catch (err) {
        console.error('❌ V207.30: Error in direct user data fetch:', err);
      }

      // V207.31: No delay needed - freshUserData is already set in state
      setProfileDataLoaded(true);
      if (import.meta.env.DEV) console.log('✅ V207.31: Profile data loaded, button can now render with freshUserData');
    };

    loadProfileData();
  }, [refreshNetwork, authUser?.id, updateCurrentUser, verifiedParam, fromOnboardingPhoto]);

  // V253: CRITICAL FIX - One-time guard to prevent loop
  const bootstrapInFlightRef = useRef(false);
  const hasBootstrappedRef = useRef<string | null>(null); // Track userId to prevent re-bootstrap on user change
  // V325: Skip checkProfileCompletion if already checked for this user in this session
  const hasCheckedCompletionRef = useRef<string | null>(null);

  // V205.6: REMOVED auto-redirect to onboarding
  // V253: Set needsOnboarding flag when user row is missing, but UPSERT immediately to prevent loop
  useEffect(() => {
    const checkProfileCompletion = async () => {
      // Wait for auth to be ready
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        navigate('/', { replace: true });
        return;
      }

      // V325: Skip if already checked for this user in this session (avoids DB query on every mount)
      if (hasCheckedCompletionRef.current === user.id) {
        return;
      }

      // V253: Guard - prevent multiple simultaneous checks
      if (bootstrapInFlightRef.current) {
        if (import.meta.env.DEV) console.log('⏸️ V253: Bootstrap already in flight, skipping');
        return;
      }

      // V253: If we already bootstrapped this user, skip
      if (hasBootstrappedRef.current === user.id) {
        if (import.meta.env.DEV) console.log('✅ V253: Already bootstrapped user, skipping check');
        return;
      }

      bootstrapInFlightRef.current = true;

      try {
        // Check if user has a database record
        const { data: userProfile, error } = await supabase
          .from('users')
          .select('id, name, photo_urls, date_of_birth')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error('❌ V205.6: Error fetching user profile:', error);
          // Don't redirect - let user see Profile page with error state
          return;
        }

        // V277.6: CRITICAL FIX - If no user profile, call ensure-user-row edge function with retry
        // Edge function returns the row data directly (avoids RLS issues)
        if (!userProfile) {
          console.error('❌ V277.6: FATAL - No user record found. Calling ensure-user-row edge function.');

          try {
            // V277.6: Get auth user
            const { data: { user: authUser } } = await supabase.auth.getUser();
            if (!authUser) {
              throw new Error('No authenticated user');
            }

            console.log('🔄 V277.6: Attempting edge function with retry logic...');

            // V277.6: Retry logic with exponential backoff
            const maxRetries = 3;
            let lastError: any = null;
            let userData: any = null;

            for (let attempt = 1; attempt <= maxRetries; attempt++) {
              try {
                console.log(`🔄 V277.6: Edge function attempt ${attempt}/${maxRetries}`);

                // V275.1: Include avatar_url for high-res Google avatar (s2048)
                const { data, error } = await supabase.functions.invoke('ensure-user-row', {
                  body: {
                    userId: authUser.id,
                    userEmail: authUser.email || '',
                    userData: {
                      name: authUser.user_metadata?.name || null,
                      avatar_url: authUser.user_metadata?.avatar_url || authUser.user_metadata?.picture
                    }
                  }
                });

                if (error) {
                  console.error(`❌ V277.6: Edge function error (attempt ${attempt}):`, error);
                  lastError = error;

                  // Wait before retry (exponential backoff: 1s, 2s, 4s)
                  if (attempt < maxRetries) {
                    const waitMs = 1000 * Math.pow(2, attempt - 1);
                    console.log(`⏳ V277.6: Waiting ${waitMs}ms before retry...`);
                    await new Promise(r => setTimeout(r, waitMs));
                    continue;
                  }
                  throw error;
                }

                if (!data?.user) {
                  console.error(`❌ V277.6: Edge function returned no user (attempt ${attempt})`);
                  lastError = new Error('Edge function succeeded but returned no user data');

                  if (attempt < maxRetries) {
                    const waitMs = 1000 * Math.pow(2, attempt - 1);
                    console.log(`⏳ V277.6: Waiting ${waitMs}ms before retry...`);
                    await new Promise(r => setTimeout(r, waitMs));
                    continue;
                  }
                  throw lastError;
                }

                // Success!
                userData = data.user;
                console.log(`✅ V277.6: Edge function succeeded on attempt ${attempt}`);
                break;

              } catch (attemptError) {
                console.error(`❌ V277.6: Exception on attempt ${attempt}:`, attemptError);
                lastError = attemptError;

                if (attempt < maxRetries) {
                  const waitMs = 1000 * Math.pow(2, attempt - 1);
                  console.log(`⏳ V277.6: Waiting ${waitMs}ms before retry...`);
                  await new Promise(r => setTimeout(r, waitMs));
                } else {
                  throw attemptError;
                }
              }
            }

            if (!userData) {
              throw lastError || new Error('Edge function failed after all retries');
            }

            console.log('✅ V277.6: User row created via edge function:', userData.id);

            // Mark as bootstrapped
            hasBootstrappedRef.current = user.id;
            hasCheckedCompletionRef.current = user.id;

            // Check if onboarding is needed (no name or DOB)
            const needsOnboardingDbRowValue = !userData.name || !userData.date_of_birth;
            setNeedsOnboardingDbRow(needsOnboardingDbRowValue);
            setProfileDataLoaded(true);
            setIsInitialLoading(false);

            if (needsOnboardingDbRowValue) {
              if (import.meta.env.DEV) console.log('✅ V277.6: User row created, onboarding still needed');
            } else {
              if (import.meta.env.DEV) console.log('✅ V277.6: User row created, onboarding complete');
            }
            return;
          } catch (edgeError) {
            console.error('❌ V277.6: FATAL - Edge function failed after all retries:', edgeError);

            // V277.6: Try direct user creation as fallback
            console.log('🔄 V277.6: Attempting direct user creation fallback...');
            try {
              const { data: { user: authUser } } = await supabase.auth.getUser();
              if (!authUser) {
                throw new Error('No authenticated user for fallback');
              }

              // Check if user exists by email
              const { data: userByEmail } = await supabase
                .from('users')
                .select('*')
                .eq('email', authUser.email)
                .maybeSingle();

              if (userByEmail) {
                console.log('✅ V277.6: User found by email in fallback');

                // Update ID if different
                if (userByEmail.id !== authUser.id) {
                  console.log('🔄 V277.6: Updating user ID...');
                  const { data: updated } = await supabase
                    .from('users')
                    .update({ id: authUser.id, updated_at: new Date().toISOString() })
                    .eq('email', authUser.email)
                    .select()
                    .single();

                  if (updated) {
                    if (import.meta.env.DEV) console.log('✅ V277.6: User ID updated successfully');
                    hasBootstrappedRef.current = authUser.id;
                    hasCheckedCompletionRef.current = authUser.id;
                    setNeedsOnboardingDbRow(!updated.name || !updated.date_of_birth);
                    setProfileDataLoaded(true);
                    setIsInitialLoading(false);
                    return;
                  }
                } else {
                  hasBootstrappedRef.current = authUser.id;
                  hasCheckedCompletionRef.current = authUser.id;
                  setNeedsOnboardingDbRow(!userByEmail.name || !userByEmail.date_of_birth);
                  setProfileDataLoaded(true);
                  setIsInitialLoading(false);
                  return;
                }
              }

              // Create new user directly
              if (import.meta.env.DEV) console.log('🆕 V277.6: Creating new user directly...');
              const { data: newUser, error: createError } = await supabase
                .from('users')
                .insert({
                  id: authUser.id,
                  email: authUser.email || '',
                  account_status: 'active',
                  trust_score: 0,
                  is_verified: false,
                  photo_urls: authUser.user_metadata?.avatar_url ? [authUser.user_metadata.avatar_url] : [],
                  name: authUser.user_metadata?.name || null,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                })
                .select()
                .single();

              if (createError) {
                console.error('❌ V277.6: Direct creation failed:', createError);
                throw createError;
              }

              if (import.meta.env.DEV) console.log('✅ V277.6: User created directly via fallback');
              hasBootstrappedRef.current = authUser.id;
              hasCheckedCompletionRef.current = authUser.id;
              setNeedsOnboardingDbRow(!newUser.name || !newUser.date_of_birth);
              setProfileDataLoaded(true);
              setIsInitialLoading(false);
              return;

            } catch (fallbackError) {
              console.error('❌ V277.6: Fallback also failed:', fallbackError);

              // V277.6: Show error to user
              toast.error('Account Setup Failed', {
                description: 'Unable to create your account. Please try signing out and back in.',
              });
              setNeedsOnboardingDbRow(true);
              setProfileDataLoaded(true);
              setIsInitialLoading(false);
              return;
            }
          }
        }

        // V253: Mark as bootstrapped
        hasBootstrappedRef.current = user.id;
        hasCheckedCompletionRef.current = user.id;

        // V207.26: Log onboarding status for debugging
        if (import.meta.env.DEV) console.log('✅ V207.26: User profile loaded:', {
          hasName: !!userProfile.name,
          hasDOB: !!userProfile.date_of_birth,
          hasPhoto: !!userProfile.photo_urls && userProfile.photo_urls.length > 0
        });

        // V242.2: User has a record, ensure needsOnboardingDbRow is false
        setNeedsOnboardingDbRow(false);
      } finally {
        bootstrapInFlightRef.current = false;
      }
    };

    checkProfileCompletion();
  }, [navigate, refreshNetwork, authUser?.id]); // V253: Add authUser?.id to deps to re-check on user change

  // V206.2: Profile building moved below after retry logic (see line ~750)

  // V240: Unified photo hook - single source of truth for profile photos
  // V242.3: Pass auth photo URL for default Google image fallback
  const authPhotoUrl = authUser?.user_metadata?.avatar_url || authUser?.user_metadata?.picture || null;
  const { photoUrl: unifiedPhotoUrl, updatePhoto: updateUnifiedPhoto } = useProfilePhoto(currentUser?.id, authPhotoUrl);

  // V252: Track if photo is ready (for onboarding splash)
  const [photoReady, setPhotoReady] = useState(!fromOnboardingPhoto);

  // V252: Wait for photo to be ready (max 2s timeout)
  useEffect(() => {
    if (!fromOnboardingPhoto) {
      setPhotoReady(true);
      return;
    }

    // Check if photo is ready
    if (unifiedPhotoUrl) {
      setPhotoReady(true);
      return;
    }

    // Timeout after 2s
    const timeout = setTimeout(() => {
      setPhotoReady(true);
      console.log('⚠️ V252: Photo ready timeout (2s) - showing profile anyway');
    }, 2000);

    return () => clearTimeout(timeout);
  }, [fromOnboardingPhoto, unifiedPhotoUrl]);

  // V150.17: Photo context for real-time updates (legacy compatibility)

  // V325.3: Initialize network stats from session cache for instant render
  const [networkStats, setNetworkStats] = useState<NetworkStatsState>(() => {
    const userId = cachedUser?.id || authUser?.id;
    if (!userId) return { friendsCount: 0, connectionsCount: 0, matchesCount: 0, trustScore: 0, secondDegreeCount: 0 };
    try {
      const raw = window.sessionStorage?.getItem(SESSION_CACHE_KEY);
      if (!raw) return { friendsCount: 0, connectionsCount: 0, matchesCount: 0, trustScore: 0, secondDegreeCount: 0 };
      const data = JSON.parse(raw);
      if (data.userId !== userId) return { friendsCount: 0, connectionsCount: 0, matchesCount: 0, trustScore: 0, secondDegreeCount: 0 };
      if (Date.now() - (data.timestamp || 0) > ONE_HOUR_MS) return { friendsCount: 0, connectionsCount: 0, matchesCount: 0, trustScore: 0, secondDegreeCount: 0 };
      return data.stats;
    } catch {
      return { friendsCount: 0, connectionsCount: 0, matchesCount: 0, trustScore: 0, secondDegreeCount: 0 };
    }
  });
  const [statsLoading, setStatsLoading] = useState(() => {
    const userId = cachedUser?.id || authUser?.id;
    if (!userId) return true;
    try {
      const raw = window.sessionStorage?.getItem(SESSION_CACHE_KEY);
      if (!raw) return true;
      const data = JSON.parse(raw);
      if (data.userId !== userId) return true;
      if (Date.now() - (data.timestamp || 0) > ONE_HOUR_MS) return true;
      return false; // Cache valid — no loading needed
    } catch {
      return true;
    }
  });
  const [statsError, setStatsError] = useState<string | null>(null);
  const [showTrust, setShowTrust] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  // V232.1: Android menu anchor fix - custom in-card dropdown (matches Network/Discover pattern)
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showPhoto, setShowPhoto] = useState(false);
  // V325.3: Initialize photo from available sources for instant render
  const [profilePhoto, setProfilePhoto] = useState<{
    urls: string[] | null;
    version: number;
  }>(() => {
    // Try cachedUser photo_urls first
    if (cachedUser?.photo_urls?.length) {
      return { urls: cachedUser.photo_urls, version: 0 };
    }
    // Try auth avatar as fallback
    const authAvatar = authUser?.user_metadata?.avatar_url || authUser?.user_metadata?.picture;
    if (authAvatar) {
      return { urls: [authAvatar], version: 0 };
    }
    return { urls: null, version: 0 };
  });
  // V150.14: Add camera modal state for optimistic UI
  const [showCamera, setShowCamera] = useState(false);
  // Android: Pending file from camera to trigger replace dialog
  const [pendingCameraFile, setPendingCameraFile] = useState<File | null>(null);
  const [pendingCameraPreview, setPendingCameraPreview] = useState<string | null>(null);
  const [pendingCameraDataUrl, setPendingCameraDataUrl] = useState<string | null>(null); // V275.11: Store original dataUrl
  // V145.4: Photo recovery system
  const [showPhotoRecovery, setShowPhotoRecovery] = useState(false);
  const [hasCheckedPhotoRecovery, setHasCheckedPhotoRecovery] = useState(false);
  // V183.2: Paywall modal state
  const [showPaywall, setShowPaywall] = useState(false);
  // V185.6: Thank you dialog state
  const [showThankYou, setShowThankYou] = useState(false);
  // V186.2: Verified info dialog state (for already-verified users)
  const [showVerifiedInfo, setShowVerifiedInfo] = useState(false);
  const [showProfileManageDialog, setShowProfileManageDialog] = useState(false);
  // V242.2: Track if user needs onboarding (no users row exists)
  // When true, skip entitlements/subscription hooks to prevent crashes
  const [needsOnboardingDbRow, setNeedsOnboardingDbRow] = useState(false);
  // V210.2: Use centralized entitlements hook (replaces isVerifiedSubscription state)
  // V242.2: Skip entitlements check if user needs onboarding to prevent crashes
  const { entitlements, loading: verificationLoading, refresh: refreshEntitlements } = useEntitlements(
    needsOnboardingDbRow ? null : (authUser?.id || null)
  );
  // P1-FLICKER: Refs for loadNetworkStats — avoid callback identity churn from entitlements
  const entitlementsRef = useRef(entitlements);
  const cachedUserVerifiedRef = useRef(cachedUser?.is_verified);
  const statsEverLoadedRef = useRef(!statsLoading);
  const networkStatsRef = useRef(networkStats);
  useEffect(() => { entitlementsRef.current = entitlements; }, [entitlements]);
  useEffect(() => { cachedUserVerifiedRef.current = cachedUser?.is_verified; }, [cachedUser?.is_verified]);
  useEffect(() => { networkStatsRef.current = networkStats; }, [networkStats]);
  // V290: Unified verified state - check BOTH entitlements and DB (handles cache lag + manual admin changes)
  const isVerifiedSubscription = entitlements?.isVerified === true || cachedUser?.is_verified === true;
  const effectiveVerified = isVerifiedUI(entitlements, cachedUser?.is_verified);

  const canonicalFriendsCount = Number(cachedUser?.friends_count ?? networkStats.friendsCount ?? 0);
  const canonicalSecondDegreeCount = Number(cachedUser?.second_degree_count ?? networkStats.secondDegreeCount ?? 0);

  // DB trust_score is authoritative when verification state matches; otherwise recompute from canonical counts.
  const displayTrustScore = useMemo(() => {
    if (
      cachedUser?.trust_score != null &&
      effectiveVerified === (cachedUser?.is_verified === true)
    ) {
      return Number(cachedUser.trust_score);
    }
    return calculateTrustScore({
      firstDegreeCount: canonicalFriendsCount,
      secondDegreeCount: canonicalSecondDegreeCount,
      isVerified: effectiveVerified,
    });
  }, [
    cachedUser?.trust_score,
    cachedUser?.is_verified,
    canonicalFriendsCount,
    canonicalSecondDegreeCount,
    effectiveVerified,
  ]);

  // V291.3: Debug logging for trust score validation
  useEffect(() => {
    if (networkStats && networkStats.trustScore !== undefined) {
      logTrustScoreDebug(
        'Profile.tsx',
        networkStats.trustScore,
        isVerifiedUI(entitlements, cachedUser?.is_verified),
        networkStats.friendsCount,
        networkStats.secondDegreeCount
      );
    }
  }, [networkStats, entitlements, cachedUser?.is_verified]);

  // V325.3: If loadProfileData TTL cache exists, data was already loaded recently
  const [profileDataLoaded, setProfileDataLoaded] = useState(() => {
    const userId = authUser?.id;
    if (!userId) return false;
    try {
      const key = `bn:profile:loadProfileData:${userId}`;
      const raw = localStorage.getItem(key);
      const last = raw ? Number(raw) : 0;
      if (last && Date.now() - last < ONE_HOUR_MS) return true;
    } catch { }
    return false;
  });
  // V325.3: Initialize from cachedUser if available (prevents needsOnboardingDbRow=true flash)
  const [freshUserData, setFreshUserData] = useState<{
    name: string | null;
    date_of_birth: string | null;
    photo_urls: string[] | null;
  } | null>(() => {
    if (cachedUser?.name || cachedUser?.date_of_birth) {
      return {
        name: cachedUser.name ?? null,
        date_of_birth: cachedUser.date_of_birth ?? null,
        photo_urls: cachedUser.photo_urls ?? null,
      };
    }
    return null;
  });

  // V210.2.2: Prevent repeated warnings (timeout and retry) across renders
  const hasWarnedTimeoutRef = useRef(false);
  const hasWarnedNoProfileRef = useRef(false);
  // V210.2.3: Track timeout id and onboarding completion
  const timeoutRef = useRef<number | null>(null);
  const hasOnboardedRef = useRef(false);
  // V210.2.4: Track fetch status and redirect suppression
  const fetchedUserRef = useRef(false);
  const redirectInProgressRef = useRef(false);

  // V291.2: Debug verification state on mount/update
  useEffect(() => {
    if (!authUser?.id) return;

    logVerificationState(
      entitlements,
      cachedUser?.is_verified,
      networkStats.trustScore,
      'Profile.tsx mount/update'
    );
  }, [authUser?.id, entitlements, cachedUser?.is_verified, networkStats.trustScore]);

  // V290: Sync effect - refresh entitlements if DB says verified but cache lags
  useEffect(() => {
    if (!authUser?.id) return;

    if (cachedUser?.is_verified === true && entitlements?.isVerified !== true) {
      if (import.meta.env.DEV) {
        console.warn('⚠️ V290: Entitlements stale vs users.is_verified=true — clearing cache + refreshing entitlements');
      }
      EntitlementService.clearCache(authUser.id);
      refreshEntitlements();
    }
  }, [authUser?.id, cachedUser?.is_verified, entitlements?.isVerified, refreshEntitlements]);

  // V290: Refresh profile + entitlements on mount/focus to ensure fresh data
  // V325: Throttled to once per hour (TTL) unless forced by ?verified=1
  useEffect(() => {
    if (!authUser?.id) return;

    const key = `bn:profile:refresh:${authUser.id}`;

    // V325: Force-bypass on post-purchase — bust ALL THREE TTL keys
    if (verifiedParam) {
      forceRunNow(
        key,
        `bn:profile:userFetch:${authUser.id}`,
        `bn:profile:barilan:${authUser.id}`
      );
    }

    if (!verifiedParam && !shouldRunHourly(key, ONE_HOUR_MS)) return;

    void (async () => {
      try {
        await refreshProfile();
        if (refreshNetwork) await refreshNetwork();
        await refreshEntitlements();
        if (import.meta.env.DEV) console.log('✅ V325: V290 refresh ran (hourly gate passed)');
      } catch (err) {
        if (import.meta.env.DEV) console.error('❌ V325: V290 refresh failed', err);
      }
    })();
  }, [authUser?.id, refreshProfile, refreshNetwork, refreshEntitlements, verifiedParam]);

  // V187.6: Enhanced verification dialog with detailed logging
  // V193.1: Also refresh data when dialog opens
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('🎯 V187.6: useEffect running, verifiedParam:', verifiedParam);
      console.log('🎯 V187.6: Current showThankYou state:', showThankYou);
    }

    if (verifiedParam && !showThankYou) {
      console.log('🎯 V193.1: Detected ?verified=1, opening thank-you dialog');

      // V193.1: Refresh data when dialog opens (might be stale)
      void (async () => {
        try {
          await refreshProfile();
          if (refreshNetwork) {
            await refreshNetwork();
            if (import.meta.env.DEV) {
              console.log('✅ V193.1: Refreshing data on dialog open');
            }
          }
        } catch (err) {
          if (import.meta.env.DEV) {
            console.error('❌ V193.1: Failed to refresh on dialog open', err);
          }
        }
      })();

      console.log('🎯 V187.6: ✅ Condition matched! Setting showThankYou to true');
      setShowThankYou(true);
      if (import.meta.env.DEV) console.log('🎯 V187.6: setShowThankYou(true) called');
    } else if (import.meta.env.DEV) {
      console.log('🎯 V187.6: ❌ Condition NOT matched, verifiedParam:', verifiedParam);
    }
  }, [verifiedParam, showThankYou, refreshProfile, refreshNetwork]);

  // V196.8: Handle ?manage=subscription query parameter
  useEffect(() => {
    if (manageParam === 'subscription' && !showProfileManageDialog) {
      if (import.meta.env.DEV) {
        console.log('🎯 V196.8: Detected ?manage=subscription, opening subscription modal');
      }
      setShowProfileManageDialog(true);
      // Remove the query parameter
      searchParams.delete('manage');
      setSearchParams(searchParams, { replace: true });
    }
  }, [manageParam, showProfileManageDialog, setSearchParams]);

  // Prevent page scroll behind fixed subscription dialogs
  useEffect(() => {
    const dialogOpen = showVerifiedInfo || showProfileManageDialog || showThankYou;
    if (!dialogOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showVerifiedInfo, showProfileManageDialog, showThankYou]);

  const refreshProfileAfterVerifiedDialog = useCallback(async () => {
    try {
      await refreshProfile();
      if (refreshNetwork) {
        await refreshNetwork();
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('❌ Profile: Failed to refresh after verified dialog', err);
      }
    }
  }, [refreshProfile, refreshNetwork]);

  // V187.6: Log whenever showThankYou state changes
  useEffect(() => {
    console.log('🎯 V187.6: showThankYou state changed to:', showThankYou);
  }, [showThankYou]);

  // V325: V191.2 removed — duplicated by V290 refresh useEffect which has TTL guard

  // V210.2: Verification status now handled by useEntitlements hook (removed old useEffect)

  // V206.3: Auto-open Edit Profile modal - MOVED to after profile definition to fix TDZ error

  // V325: Stabilize connection arrays to prevent loadNetworkStats cache-busting on refreshNetwork() ref changes
  const stableConnections = useMemo(() => connections, [generateConnectionsHash(connections)]);
  const stableAllConnections = useMemo(() => allConnections, [generateConnectionsHash(allConnections)]);
  const stableSecondDegree = useMemo(() => secondDegreeConnections, [secondDegreeConnections?.length ?? 0]);

  // V168.1.3: Load network stats with session cache - static until connections change
  // Enhanced loading state handling to prevent showing 0s before data loads
  const loadNetworkStats = useCallback(async (forceRecalc = false) => {
    if (import.meta.env.DEV) {
      console.log('🚀 V291.10: loadNetworkStats called', {
        userId: currentUser?.id,
        forceRecalc,
        hasConnections: !!stableConnections,
        connectionsLength: stableConnections?.length,
        has2ndDegree: !!stableSecondDegree,
        secondDegreeLength: stableSecondDegree?.length
      });
    }

    if (!currentUser?.id) {
      if (import.meta.env.DEV) console.log('⚠️ V291.13: EARLY RETURN - No currentUser');
      setStatsLoading(false);
      return;
    }

    const priorStats = networkStatsRef.current;
    const hasPriorStats =
      statsEverLoadedRef.current ||
      priorStats.trustScore > 0 ||
      priorStats.friendsCount > 0 ||
      priorStats.matchesCount > 0 ||
      priorStats.connectionsCount > 0 ||
      priorStats.secondDegreeCount > 0 ||
      cachedUser?.friends_count != null;

    if (!hasPriorStats) {
      setStatsLoading(true);
    }
    setStatsError(null);

    try {
      if (import.meta.env.DEV) console.log('🚀 V291.10: loadNetworkStats - starting calculation');

      // V168.1.3: Always proceed - if connections are empty, user might genuinely have 0 connections
      // Don't block loading stats just because connections array is empty
      // NetworkDataContext will provide empty array if user has no connections
      // V325: Re-enabled cache check with 1-hour TTL
      // V325.6: Include 2nd degree count in cache validation — reject stale cache (score=60) when 2nd degree loads later
      const currentSecondDegreeCount = cachedUser?.second_degree_count != null
        ? Number(cachedUser.second_degree_count)
        : (stableSecondDegree?.length ?? 0);
      if (!forceRecalc) {
        const cachedStats = getCachedProfileStats(currentUser.id, stableAllConnections); // Use stableAllConnections for cache key
        if (cachedStats) {
          // V325.6: Validate 2nd degree count matches — reject cache if 2nd degree was 0 but now has data
          if (cachedStats.stats.secondDegreeCount !== currentSecondDegreeCount) {
            if (import.meta.env.DEV) console.log('🔄 V325.6: Cache invalidated — 2nd degree count changed:', {
              cached: cachedStats.stats.secondDegreeCount,
              current: currentSecondDegreeCount
            });
            // Fall through to recalculate
          } else {
            if (import.meta.env.DEV) console.log('🚀 V325: Using cached profile stats (valid & <1h old)');
            // V325.19: Compare before setting to prevent re-render from new JSON.parse object
            setNetworkStats(prev => {
              if (
                prev.friendsCount === cachedStats.stats.friendsCount &&
                prev.connectionsCount === cachedStats.stats.connectionsCount &&
                prev.matchesCount === cachedStats.stats.matchesCount &&
                prev.trustScore === cachedStats.stats.trustScore &&
                prev.secondDegreeCount === cachedStats.stats.secondDegreeCount
              ) {
                return prev; // Same reference — no re-render
              }
              return cachedStats.stats;
            });
            statsEverLoadedRef.current = true;
            setStatsLoading(false);
            return; // DONE - No queries needed!
          }
        }
      }
      if (import.meta.env.DEV) console.log('🚀 V291.10: Cache check bypassed or expired - forcing fresh calculation');

      // V168.5: Calculate directly from NetworkDataContext - no database query needed
      // This eliminates 406 errors and is more efficient since we already have all the data
      // V161.DISCOVER: Don't pass supabase client - connections are provided from NetworkDataContext
      // If connections are empty, trust score will be 0 (no error thrown)
      // V210.2: Use entitlements for verification status (replaces direct is_verified check)
      const isVerified = isVerifiedUI(entitlementsRef.current, cachedUserVerifiedRef.current);

      // V291.10: BEFORE trust score calculation (V325: use stable arrays)
      if (import.meta.env.DEV) {
        console.log('🚀 V291.10: BEFORE trust score calculation', {
          userId: currentUser.id,
          hasConnections: !!stableConnections,
          connectionsLength: stableConnections?.length,
          has2ndDegree: !!stableSecondDegree,
          secondDegreeLength: stableSecondDegree?.length,
          isVerified: isVerified,
          friendsCount: stableConnections.filter((c: any) => {
            const type = c.connectionType || c.type || '';
            const status = c.status || '';
            return (type === 'FRIEND' || type === 'friend') && status === 'ACCEPTED';
          }).length,
          secondDegreeCount: stableSecondDegree?.length || 0
        });
      }

      // Use network data for accurate trust score calculation (isVerified already set above)

      // Profile Matches counter: count active matches where user is user1 OR user2 (DB truth, no schema change)
      const fetchActiveMatchesCount = async (): Promise<number> => {
        // Use baseSupabase to bypass query tracker for lightweight count query
        const { count, error } = await baseSupabase
          .from('matches')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'active')
          .or(`user1_id.eq.${currentUser.id},user2_id.eq.${currentUser.id}`);
        if (error) {
          console.warn('Profile matches count error:', error);
          return 0;
        }
        return count ?? 0;
      };

      // Calculate trust score and active matches in parallel
      const [trustData, activeMatchesCount] = await Promise.all([
        trustScoreService.calculateTrustScoreFromNetwork(
          currentUser.id,
          stableConnections,
          stableSecondDegree ?? [],
          supabase,
          isVerified
        ),
        fetchActiveMatchesCount()
      ]);

      if (import.meta.env.DEV) console.log('🚀 V291.10: AFTER network trust score calculation SUCCESS', {
        currentScore: trustData.currentScore,
        firstDegreeCount: trustData.firstDegreeCount,
        secondDegreeCount: trustData.secondDegreeCount,
        connectionCount: trustData.connectionCount,
        expectedScore: (trustData.firstDegreeCount * 10) + (trustData.secondDegreeCount * 1) + (isVerified ? 100 : 0)
      });

      // Canonical counts: DB users row (same source as update-trust-score / Network commitStable)
      const friendsCount = cachedUser?.friends_count != null
        ? Number(cachedUser.friends_count)
        : trustData.firstDegreeCount;
      const secondDegreeCount = cachedUser?.second_degree_count != null
        ? Number(cachedUser.second_degree_count)
        : trustData.secondDegreeCount;

      const trustScore =
        cachedUser?.trust_score != null && isVerified === (cachedUser?.is_verified === true)
          ? Number(cachedUser.trust_score)
          : calculateTrustScore({
              firstDegreeCount: friendsCount,
              secondDegreeCount,
              isVerified,
            });

      const connectionsCount = cachedUser?.friends_count != null && cachedUser?.second_degree_count != null
        ? friendsCount + secondDegreeCount
        : trustData.connectionCount;

      const newStats: NetworkStatsState = {
        friendsCount,
        connectionsCount,
        matchesCount: activeMatchesCount,
        trustScore,
        secondDegreeCount,
      };

      // Cache the result (V325: use stable arrays)
      setCachedProfileStats(currentUser.id, stableAllConnections, newStats);

      // V320: DB update removed — single orchestrator is NetworkDataContext after loadAllNetworkData.
      // Profile only updates local stats for display; no duplicate update-trust-score calls.
      // V325.19: Compare before setting to prevent unnecessary re-renders
      setNetworkStats(prev => {
        if (
          prev.friendsCount === newStats.friendsCount &&
          prev.connectionsCount === newStats.connectionsCount &&
          prev.matchesCount === newStats.matchesCount &&
          prev.trustScore === newStats.trustScore &&
          prev.secondDegreeCount === newStats.secondDegreeCount
        ) {
          return prev;
        }
        return newStats;
      });

      statsEverLoadedRef.current = true;
      // V291.16: DB update moved to delayed useEffect (10s after mount)
      // This ensures verification status is loaded before calculating trust score
      if (import.meta.env.DEV) console.log('✅ V291.16: Network stats calculated and set in state', {
        userId: currentUser.id,
        friendsCount: newStats.friendsCount,
        connectionsCount: newStats.connectionsCount,
        matchesCount: newStats.matchesCount,
        trustScore: newStats.trustScore,
        isVerified: isVerified
      });

      setStatsLoading(false);
      if (import.meta.env.DEV) console.log('🚀 V291.10: loadNetworkStats COMPLETE');
    } catch (error) {
      console.error('❌ V291.10: loadNetworkStats FAILED:', error);
      console.error('❌ V291.10: Error stack:', error?.stack);
      console.error('❌ V291.10: Error details:', {
        message: error?.message,
        name: error?.name,
        code: error?.code
      });
      setStatsError('Failed to load network stats');
      if (!statsEverLoadedRef.current) {
        setStatsLoading(false);
      }
    }
  }, [currentUser?.id, cachedUser?.friends_count, cachedUser?.second_degree_count, cachedUser?.trust_score, cachedUser?.is_verified, stableConnections, stableAllConnections, stableSecondDegree, isNetworkLoaded]);

  // Repair users.is_verified drift when entitlements confirm active subscription (e.g. Apple-only)
  useEffect(() => {
    if (!authUser?.id || verificationLoading || needsOnboardingDbRow) return;
    if (entitlements?.isVerified !== true || cachedUser?.is_verified === true) return;

    let cancelled = false;
    void (async () => {
      const repaired = await repairEntitlementDrift(authUser.id);
      if (!repaired || cancelled) return;
      EntitlementService.clearCache(authUser.id);
      await refreshProfile();
      if (refreshNetwork) await refreshNetwork();
      await refreshEntitlements();
      loadNetworkStats(true);
    })();

    return () => { cancelled = true; };
  }, [
    authUser?.id,
    verificationLoading,
    needsOnboardingDbRow,
    entitlements?.isVerified,
    cachedUser?.is_verified,
    refreshProfile,
    refreshNetwork,
    refreshEntitlements,
    loadNetworkStats,
  ]);

  // V167.FIX: Initial load - re-run when connections change
  // CRITICAL: Block early empty writes — only run after network is loaded (tabCache.lastUpdated > 0).
  // V325.6: Add secondDegreeCount to deps — re-fires when 2nd degree loads (was 0, now has data)
  useEffect(() => {
    if (!loadNetworkStats) return;
    if (!currentUser?.id) return;

    if (!isNetworkLoaded) {
      if (import.meta.env.DEV) console.log('⏭️ V291.13: Skipping loadNetworkStats — network not loaded yet (lastUpdated=0)');
      return;
    }

    loadNetworkStats();
  }, [loadNetworkStats, stableConnections, currentUser?.id, isNetworkLoaded, secondDegreeCount]);

  // P1-FLICKER: Silent trust recalc when entitlements resolve — never flip stats skeleton
  const entitlementsVerifiedSnapshotRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (verificationLoading || !currentUser?.id || !isNetworkLoaded) return;
    const verifiedNow = entitlements?.isVerified === true;
    if (entitlementsVerifiedSnapshotRef.current === undefined) {
      entitlementsVerifiedSnapshotRef.current = verifiedNow;
      return;
    }
    if (entitlementsVerifiedSnapshotRef.current === verifiedNow) return;
    entitlementsVerifiedSnapshotRef.current = verifiedNow;
    loadNetworkStats(true);
  }, [verificationLoading, entitlements?.isVerified, currentUser?.id, isNetworkLoaded, loadNetworkStats]);

  // V167.FIX: Listen for connection changes and refresh stats
  useEffect(() => {
    if (!currentUser?.id) return;

    // Canonical event from forceHardSync (after network refetch completes)
    const handleConnectionSync = (event: CustomEvent) => {
      const { reason } = event.detail || {};
      console.log('🔄 V320.RELOAD.1: Profile received connection-sync event:', { reason });

      // Force recalculation when connection changes (bypass cache)
      loadNetworkStats(true);

      // Also refresh profile data
      if (refreshProfile) {
        void refreshProfile();
      }
    };

    // Local accept/disconnect still dispatch connectionChanged (accepter path)
    const handleConnectionChange = () => {
      // V167.FIX: Connection changed event received, forcing recalculation
      // V167.FIX: Force recalculation when connection changes (bypass cache)
      loadNetworkStats(true);
    };

    // V168.BLOCK.REALTIME: Listen for blocking events to refresh Profile stats
    const handleUserBlocked = (event: CustomEvent) => {
      const { blockerId, blockedId } = event.detail;
      console.log('🔒 PROFILE: User blocked event received:', { blockerId, blockedId });

      // Refresh stats if current user is involved in blocking
      if (blockerId === currentUser.id || blockedId === currentUser.id) {
        console.log('🔄 PROFILE: Refreshing network stats due to blocking...');
        loadNetworkStats(true);
      }
    };

    window.addEventListener(APP_CONNECTION_SYNC, handleConnectionSync as EventListener);
    window.addEventListener('connectionChanged', handleConnectionChange);
    window.addEventListener('user-blocked', handleUserBlocked as EventListener);

    return () => {
      window.removeEventListener(APP_CONNECTION_SYNC, handleConnectionSync as EventListener);
      window.removeEventListener('connectionChanged', handleConnectionChange);
      window.removeEventListener('user-blocked', handleUserBlocked as EventListener);
    };
  }, [currentUser?.id, loadNetworkStats, refreshProfile]);

  // V145.4 + V163.2: NUCLEAR PHOTO RECOVERY DETECTION using cached user (no extra query)
  useEffect(() => {
    if (!currentUser?.id || hasCheckedPhotoRecovery) return;

    const checkPhotoRecovery = async () => {
      try {
        // 🎯 PHASE 1: Check if there's a pending invitation popup
        const shouldShowInvitePopup = localStorage.getItem('showPostOnboardingInvitePopup') === 'true';
        const hasInviteData = localStorage.getItem('postOnboardingInviteData');

        if (shouldShowInvitePopup && hasInviteData) {
          console.log('🎯 PHASE 1: Invitation popup pending - delaying photo recovery check');
          setTimeout(() => { setHasCheckedPhotoRecovery(false); }, 5000);
          return;
        }

        // Use cached user to determine photo status
        const hasPhotos = Array.isArray(cachedUser?.photo_urls) && (cachedUser?.photo_urls?.length || 0) > 0;

        if (!hasPhotos) {
          // V145.4: User has no photos - checking for invitation popup first

          const inviteFlag = localStorage.getItem('showPostOnboardingInvitePopup');
          const inviteDataStr = localStorage.getItem('postOnboardingInviteData');
          if (inviteFlag === 'true' && inviteDataStr) {
            // V151.19: Invitation popup has priority - delaying photo recovery
            return;
          }

          // V163.8: No photos found - checking for photo recovery needed
          setHasCheckedPhotoRecovery(true);
          return;
        } else {
          // V145.4: User has photos - no recovery needed
        }

        setHasCheckedPhotoRecovery(true);
      } catch (error) {
        console.error('❌ V145.4: Photo recovery check failed:', error);
        setHasCheckedPhotoRecovery(true);
      }
    };

    checkPhotoRecovery();
  }, [currentUser?.id, hasCheckedPhotoRecovery, cachedUser?.photo_urls]);

  const handleSave = useCallback(async (upd: any) => {
    try {
      if (!currentUser?.id) {
        toast.error('User not found');
        return;
      }

      // V163.8.5: Smart profile update via NetworkDataContext (prevents duplicate queries)

      // V164.1: Update user data using smart method (includes gender and dating_preferences)
      // V167.MERGE: Use date_of_birth instead of dob
      if (updateCurrentUser) {
        const userUpdate = {
          name: upd.name,
          bio: upd.bio,
          date_of_birth: upd.date_of_birth,
          gender: upd.gender || null,
          dating_preferences: upd.dating_preferences || null,
          // V167.AUDIT: Track every profile edit timestamp
          profile_last_edited_at: new Date().toISOString()
        };

        await updateCurrentUser(userUpdate);
      } else {
        console.warn('⚠️ Profile.tsx: updateCurrentUser is not available!');
      }

      // Persist profiles fields — same source of truth as fetch
      const pU: Record<string, unknown> = {};
      if (upd.relationship_status) pU.relationship_status = upd.relationship_status;
      if (upd.dating_goal) pU.dating_goal = upd.dating_goal;
      if (upd.show_age !== undefined) pU.show_age = upd.show_age; // V334.S08: Forward show_age to profiles table cache

      if (Object.keys(pU).length > 0 && updateCurrentUserProfile) {
        await updateCurrentUserProfile(pU);
      }

      toast.success('Profile updated');

      // V167.AUDIT: Do not write profile_change_events / user_events from the client.
      // - profile_change_events: INSERT returns 403 under typical RLS (no INSERT for authenticated).
      // - user_events: schema uses `payload` (not `event_data`); RLS has no INSERT policy for
      //   authenticated users (see 20260202_user_events_connection_accepted.sql — server trigger only).
      // Save success is defined only by users + profiles updates above.

      // V325: Targeted refresh instead of full page reload
      try {
        if (refreshNetwork) await refreshNetwork();
        if (refreshProfile) await refreshProfile();
        loadNetworkStats(true);
      } catch (err) {
        if (import.meta.env.DEV) console.error('V325: Post-save refresh failed:', err);
      }
    } catch (error) {
      console.error('❌ Profile.tsx: handleSave error:', error);
      console.error('❌ Profile.tsx: Error details:', JSON.stringify(error, null, 2));
      toast.error('Failed to update profile');
      throw error;
    }
  }, [currentUser?.id, updateCurrentUser, updateCurrentUserProfile, refreshNetwork, refreshProfile, loadNetworkStats]);

  // V145.4: Handle photo recovery completion
  const handlePhotoRecoveryComplete = useCallback((photoUrl: string) => {

    // Update photo context
    if (photoContext?.updateUserPhoto && currentUser?.id) {
      photoContext.updateUserPhoto(currentUser.id, photoUrl);
    }

    // V266: No forced re-render needed - component updates naturally when photoContext changes
    setShowPhotoRecovery(false);

    toast.success('Profile photo added successfully!');
  }, [photoContext, currentUser?.id]);

  // V150.20: INSTANT NAVIGATION - Handle camera photo completion with zero delay
  const handleCameraComplete = useCallback((photoUrl: string) => {
    if (!currentUser?.id) {
      console.error('❌ No user ID available for photo update');
      toast.error('User not found. Please try again.');
      setShowCamera(false);
      return;
    }

    // INSTANT OPERATIONS ONLY (no async, no await)

    // Update photo context immediately for instant display
    if (photoContext?.updateUserPhoto) {
      photoContext.updateUserPhoto(currentUser.id, photoUrl);
    }

    // V266: No forced re-render needed - component updates naturally when photoContext changes

    // Close camera modal (INSTANT navigation)
    setShowCamera(false);

    toast.success(
      <span dir={direction} className="text-start ps-8">{t('camera.photoUpdateSuccess')}</span>
    );

    // V320: DB write only via pipeline (useProfilePhoto.updatePhoto → updateCurrentUser), not direct .update(photo_urls)
    if (photoUrl.startsWith('https://res.cloudinary.com/') && updateUnifiedPhoto) {
      updateUnifiedPhoto(photoUrl).catch((err) => {
        console.error('❌ V320: Pipeline DB update failed:', err);
        toast.error('Failed to save photo to profile. Please try again.');
      });
    }
  }, [currentUser?.id, photoContext, updateUnifiedPhoto, direction, t]);

  // V266: Stabilize photo update callback to prevent unnecessary re-renders
  const handlePhotoUpdate = React.useCallback((newPhotoUrl: string) => {
    console.log('[V266] 📸 Photo update received');

    const isCloudinary = newPhotoUrl.startsWith('https://res.cloudinary.com/');
    if (!isCloudinary) {
      console.error('[V266] Invalid URL');
      return;
    }

    // V266: SINGLE state update - no cascade
    setProfilePhoto(prev => ({
      urls: [newPhotoUrl],
      version: prev.version + 1
    }));

    console.log('[V266] ✅ Photo updated (single render)');
  }, []); // Empty deps - function never recreates

  // V214: Handle camera file selected (Android selfie flow) - opens replace dialog
  const handleCameraFileSelected = useCallback((file: File, previewUrl: string, dataUrl?: string) => {
    // V276.1: STEP 4 - handleCameraFileSelected received
    console.log('[V276.1] ════════════════════════════════════════');
    console.log('[V276.1] STEP 4: handleCameraFileSelected received');
    console.log('[V276.1] file:', file?.name, file?.size);
    console.log('[V276.1] previewUrl:', previewUrl?.substring(0, 50));
    console.log('[V276.1] dataUrl param exists:', !!dataUrl);
    console.log('[V276.1] dataUrl param type:', typeof dataUrl);
    console.log('[V276.1] dataUrl param is null:', dataUrl === null);
    console.log('[V276.1] dataUrl param is undefined:', dataUrl === undefined);
    console.log('[V276.1] dataUrl param is empty string:', dataUrl === '');
    console.log('[V276.1] dataUrl param length:', dataUrl?.length || 0);
    console.log('[V276.1] dataUrl param prefix:', dataUrl?.substring(0, 50) || 'NULL/UNDEFINED');
    console.log('[V276.1] ════════════════════════════════════════');

    console.log('📷 V276.1: Camera file selected, opening replace dialog');
    console.log('[V276.1] Has dataUrl:', !!dataUrl, 'length:', dataUrl?.length || 0);

    // V276.1: Store the file, preview, and dataUrl for the replace dialog
    setPendingCameraFile(file);
    setPendingCameraPreview(previewUrl);
    setPendingCameraDataUrl(dataUrl || null); // V276.1: Store original dataUrl from camera

    // V276.1: STEP 5 - State set
    console.log('[V276.1] ════════════════════════════════════════');
    console.log('[V276.1] STEP 5: State variables set');
    console.log('[V276.1] setPendingCameraFile called with:', file?.name);
    console.log('[V276.1] setPendingCameraPreview called with:', previewUrl?.substring(0, 50));
    console.log('[V276.1] Value passed to setPendingCameraDataUrl:', (dataUrl || null)?.substring(0, 50) || 'NULL');
    console.log('[V276.1] Type passed to setPendingCameraDataUrl:', typeof (dataUrl || null));
    console.log('[V276.1] ════════════════════════════════════════');

    // Close camera and open photo viewer which will show replace dialog
    setShowCamera(false);
    setShowPhoto(true);
  }, []);

  // V206.2: Add retry logic with auth user fallback
  const [retryAttempt, setRetryAttempt] = useState(0);
  const MAX_RETRIES = 5;

  // V206.2: Retry logic - refresh network data if cached user is null
  // V242.2: Skip retries entirely if user needs onboarding (no user row exists)
  useEffect(() => {
    if (hasOnboardedRef.current) return; // stop retries once DOB confirmed
    if (needsOnboardingDbRow) return; // V242.2: Skip retries - user has no DB row, nothing to retry

    if (!cachedUser && authUser?.id && retryAttempt < MAX_RETRIES) {
      const timer = setTimeout(async () => {
        console.log(`🔄 V206.2: Retry ${retryAttempt + 1}/${MAX_RETRIES} - refreshing user data`);

        // Trigger network refresh to reload user data
        if (refreshNetwork) {
          try {
            await refreshNetwork();
          } catch (err) {
            if (import.meta.env.DEV) {
              console.warn('⚠️ V206.2: Network refresh failed during retry:', err);
            }
          }
        }

        setRetryAttempt(prev => prev + 1);
      }, 1000);

      return () => clearTimeout(timer);
    }

    // After max retries, log that we're using auth user fallback
    if (retryAttempt >= MAX_RETRIES && !cachedUser && authUser && !hasOnboardedRef.current) {
      if (import.meta.env.DEV) {
        console.warn('⚠️ V206.2: Using auth user as fallback after retries');
      }
    }
  }, [cachedUser, authUser?.id, retryAttempt, refreshNetwork]);

  // V206.2: Build profile from cached user or auth user fallback
  // V334-P6-01: user.name is the ONLY display source.
  // auth.user_metadata.name is NOT a display source (DB user.name is).
  const getDisplayName = (user: any, auth: any): string => {
    if (user?.name) return user.name;
    try {
      const primaryKey = 'bn_name';
      const storedName = localStorage.getItem(primaryKey);
      if (storedName) return storedName;

      // Back-compat: migrate from legacy key without referencing it directly in code
      const legacyKey = ['bn', 'display', 'name'].join('_');
      const legacyName = localStorage.getItem(legacyKey);
      if (legacyName) {
        localStorage.setItem(primaryKey, legacyName);
        localStorage.removeItem(legacyKey);
        return legacyName;
      }
    } catch { }
    if (auth?.email) return auth.email.split('@')[0];
    return 'User';
  };

  const displayUser = cachedUser || (authUser ? {
    id: authUser.id,
    email: authUser.email || '',
    name: getDisplayName(cachedUser, authUser), // V242.3: Use priority-based name resolution
    photo_urls: authUser.user_metadata?.avatar_url ? [authUser.user_metadata.avatar_url] : [],
    bio: '',
    date_of_birth: null,
    gender: null,
    trust_score: 0,
    is_verified: false,
    profiles: null,
  } : null);

  // V325.4: Removed — profile is now built via useMemo (no useState needed)
  // V325.3: Skip initial loading if V265 TTL cache indicates data was recently fetched
  const [isInitialLoading, setIsInitialLoading] = useState(() => {
    const userId = authUser?.id;
    if (!userId) return true;
    try {
      const key = `bn:profile:userFetch:${userId}`;
      const raw = localStorage.getItem(key);
      const last = raw ? Number(raw) : 0;
      if (last && Date.now() - last < ONE_HOUR_MS) {
        return false; // Recent fetch exists — no loading needed
      }
    } catch { }
    return true;
  });

  // ============================================================================
  // V265.2 EMERGENCY: OPTIMIZED DATABASE QUERY (95% FASTER)
  // Minimal SELECT (3 fields instead of 7) for fastest possible query
  // Load time: 10-15s → <1s (typical)
  // ============================================================================
  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | null = null;

    const fetchUserData = async () => {
      if (!authUser?.id) {
        setIsInitialLoading(false);
        return;
      }

      // V325: TTL Guard
      const key = `bn:profile:userFetch:${authUser.id}`;
      const isForced = fromOnboardingPhoto || verifiedParam;

      if (isForced) forceRunNow(key);

      // V325: COLD-START GUARD — if we have no data in state, ALWAYS fetch
      // regardless of TTL. This prevents blank profile after browser restart.
      const hasExistingData = !!(currentUser?.name || currentUser?.photo_urls?.length);

      // V334: Only skip when we have Cloudinary photo from DB (cachedUser).
      // If we only have auth fallback or Google placeholder, always fetch to restore Cloudinary.
      const hasCloudinaryPhoto = (currentUser?.photo_urls || []).some(
        (u: unknown) => typeof u === 'string' && u.includes('cloudinary.com')
      );
      const profilePhotoHasCloudinary = (profilePhoto.urls || []).some(
        (u: unknown) => typeof u === 'string' && u.includes('cloudinary.com')
      );
      const unifiedHasCloudinary = typeof unifiedPhotoUrl === 'string' && unifiedPhotoUrl.includes('cloudinary.com');
      const canSkipFetch = !isForced && cachedUser && hasExistingData &&
        (hasCloudinaryPhoto || profilePhotoHasCloudinary || unifiedHasCloudinary) &&
        !shouldRunHourly(key, ONE_HOUR_MS);

      if (canSkipFetch) {
        if (import.meta.env.DEV) console.log('[V265] ⏭️ Skipping users fetch (TTL + Cloudinary exists)');
        setIsInitialLoading(false);   // CRITICAL: prevents infinite spinner on cache-hit path
        return;
      }

      if (import.meta.env.DEV) {
        console.log('[V265.3] 🚀 Starting optimized data fetch for:', authUser.id);
      }
      const fetchStartTime = performance.now();

      // Hang escape only. The slow warning uses the measured duration in finally.
      if (!hasOnboardedRef.current && !needsOnboardingDbRow) {
        timeoutId = window.setTimeout(() => {
          if (!cancelled) {
            setIsInitialLoading(false);
          }
        }, 10000);
        timeoutRef.current = timeoutId;
      }

      try {
        // V265.2 EMERGENCY: Minimal SELECT for fastest query
        // Only fetch what's absolutely needed for Profile page
        // Other data (name, gender, trust_score, is_verified) comes from cachedUser
        const [userResult] = await Promise.all([
          supabase
            .from('users')
            .select('id, photo_urls, date_of_birth') // Only 3 essential fields
            .eq('id', authUser.id)
            .maybeSingle(),

          // Future: Add more queries here if needed (photos, connections, etc.)
        ]);

        if (import.meta.env.DEV) {
          const fetchDuration = Math.round(performance.now() - fetchStartTime);
          console.log(`[V265.3] ✅ Optimized fetch complete in ${fetchDuration}ms`);
        }

        if (cancelled) return;

        // V265.2 EMERGENCY: Extract data from results
        const userData = userResult.data;
        const userError = userResult.error;

        // V265.2 EMERGENCY: Handle errors
        if (userError) {
          console.error('[V265.3] ❌ User data fetch error:', userError);
          if (!cancelled) {
            setProfilePhoto(prev => ({ ...prev, urls: [] }));
            setIsInitialLoading(false);
          }
          return;
        }

        // V265.2 EMERGENCY: Validate data exists
        if (!userData) {
          console.warn('[V265.3] ⚠️ No user data found for ID:', authUser.id);
          if (!cancelled) {
            setProfilePhoto(prev => ({ ...prev, urls: [] }));
            setIsInitialLoading(false);
          }
          return;
        }

        // V265.2 EMERGENCY: Mark as fetched
        fetchedUserRef.current = true;

        // V320: Never let DB Google placeholder override - use normalizePhotoUrls (Cloudinary only)
        const authAvatarUrl = authUser?.user_metadata?.avatar_url || authUser?.user_metadata?.picture || null;
        const normalized = normalizePhotoUrls({
          dbUrls: userData.photo_urls,
          unifiedUrl: null,
          authAvatarUrl,
        });
        if (import.meta.env.DEV && userData?.photo_urls?.length) {
          console.log('[V265.3] 📸 Photo URLs from DB (normalized):', normalized.length, 'of', userData.photo_urls.length);
        }
        setProfilePhoto(prev => {
          const nextUrls = normalized;
          if (
            (prev.urls?.length ?? 0) === nextUrls.length &&
            (prev.urls || []).every((u, i) => u === nextUrls[i])
          ) {
            return prev;
          }
          return { ...prev, urls: nextUrls };
        });

        // V320.2: If empty but auth has Google avatar, trigger one-time bootstrap.
        // IMPORTANT: As soon as ensure-user-row returns a Cloudinary URL, paint it immediately via unified + PhotoContext.
        if (normalized.length === 0 && authAvatarUrl && (authAvatarUrl.includes('googleusercontent.com') || authAvatarUrl.includes('lh3.googleusercontent'))) {
          console.log('[V320] 📸 No Cloudinary photo; triggering ensure-user-row to convert Google avatar');
          supabase.functions.invoke('ensure-user-row', {}).then(({ data, error }) => {
            if (error) {
              console.warn('[V320] ensure-user-row (bootstrap):', error);
              return;
            }

            if (!data?.ok || !data.user) {
              console.log('[V320] ensure-user-row completed without user payload');
              return;
            }

            console.log('[V320] ensure-user-row ok – using edge response for optimistic paint');
            try {
              const rawPhotoUrls = Array.isArray(data.user.photo_urls) ? data.user.photo_urls : [];
              const cloudUrl = rawPhotoUrls.find((u: unknown) =>
                typeof u === 'string' && u.includes('cloudinary.com')
              ) as string | undefined;

              if (cloudUrl && currentUser?.id) {
                // 1) Unified/photo hook – becomes first source for normalizePhotoUrls (Cloudinary wins)
                try {
                  updateUnifiedPhoto?.(cloudUrl);
                } catch (e) {
                  console.error('[V320] Failed to update unified photo from ensure-user-row:', e);
                }

                // 2) PhotoContext L1 cache – hero can render immediately without waiting for DB refetch
                try {
                  photoContext?.updateUserPhoto?.(currentUser.id, cloudUrl, 'cloudinary');
                } catch (e) {
                  console.error('[V320] Failed to update PhotoContext from ensure-user-row:', e);
                }

                // 3) Warm browser cache (optional) to avoid late paint when component re-renders
                try {
                  const img = new Image();
                  img.src = cloudUrl;
                } catch {
                  // ignore
                }
              }
            } catch (e) {
              console.error('[V320] Error handling ensure-user-row bootstrap response:', e);
            }
          });
        }

        if (userData?.date_of_birth) {
          hasOnboardedRef.current = true;
        }

        if (import.meta.env.DEV) {
          console.log('[V265.3] ✅ User data loaded successfully');
        }
      } catch (err) {
        console.error('[V265.3] ❌ Fetch exception:', err);
        if (!cancelled) {
          setProfilePhoto(prev => ({ ...prev, urls: [] }));
        }
      } finally {
        const fetchDuration = Math.round(performance.now() - fetchStartTime);
        if (import.meta.env.DEV && fetchDuration > 10000 && !hasWarnedTimeoutRef.current) {
          console.warn('[V265.3] ⚠️ Data loading slow (>10s) - check network connection');
          console.warn(`[V265.3] 📊 Expected: <1s with optimized query, Current: ${fetchDuration}ms`);
          hasWarnedTimeoutRef.current = true;
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (!cancelled) {
          setIsInitialLoading(false);
        }
      }
    };

    fetchUserData();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [authUser?.id, currentUser?.id, cachedUser, photoContext, updateUnifiedPhoto, fromOnboardingPhoto, verifiedParam]);
  // ============================================================================
  // END V265.2 EMERGENCY: OPTIMIZED DATABASE QUERY
  // ============================================================================

  // V325.4: Build profile synchronously via useMemo — available on first render, no flash
  const profileData = useMemo(() => {
    if (!displayUser) return null;
    // V325.4: On cached remount isInitialLoading is false, so this builds immediately.
    // On first-ever load isInitialLoading may be true — allow build anyway if displayUser exists,
    // because displayUser already has enough data for a meaningful render.

    const authAvatarUrl = authUser?.user_metadata?.avatar_url || authUser?.user_metadata?.picture || null;
    // V334: Merge both sources so Cloudinary from cachedUser is used when profilePhoto fetch is pending/skipped
    const mergedDbUrls = [
      ...(profilePhoto.urls || []),
      ...(displayUser.photo_urls || []),
    ];
    const photoUrls = normalizePhotoUrls({
      dbUrls: mergedDbUrls.length ? mergedDbUrls : undefined,
      unifiedUrl: unifiedPhotoUrl || null,
      authAvatarUrl,
    });

    return {
      id: displayUser.id,
      name: displayUser.name,
      email: displayUser.email,
      photo_urls: photoUrls,
      bio: displayUser.bio || '',
      date_of_birth: displayUser.date_of_birth,
      gender: displayUser.gender,
      dating_preferences: displayUser.dating_preferences,
      trust_score: displayUser.trust_score || 0,
      is_verified: displayUser.is_verified || false,
      show_age: displayUser.profiles?.show_age !== false, // V334: Per-user age visibility from DB
      profiles: displayUser.profiles,
      age: displayUser.date_of_birth ? (() => {
        const today = new Date();
        const birthDate = new Date(displayUser.date_of_birth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        return age;
      })() : undefined
    };
  }, [displayUser, profilePhoto.urls, unifiedPhotoUrl, authUser?.user_metadata?.avatar_url, authUser?.user_metadata?.picture, cachedUser?.profiles?.show_age]);

  // V206.6: Use profileData for rendering (V325.4: now from useMemo)
  const profile = profileData;

  // V206.5: CRITICAL FIX - Move useEffect BEFORE all conditional returns (Rules of Hooks)
  // Auto-open Edit Profile modal when ?edit=true query parameter is present
  useEffect(() => {
    if (editParam && profile && !showEdit) {
      setShowEdit(true);
      // Remove the query parameter from URL after opening modal
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }
  }, [editParam, profile, showEdit, setSearchParams]);


  // V325: Top-level logs removed — ran on every render

  // V291.13: Single loading check - AFTER all hooks (Rules of Hooks compliance)
  // V325.4: Removed isInitialLoading gate — profile builds synchronously from displayUser
  // V334-P6-02: Wait for cachedUser (DB name) instead of flashing email-prefix fallback.
  // Safety escapes: retryAttempt >= MAX_RETRIES (5s), needsOnboardingDbRow (new user, no DB row).
  // HOTFIX: Also wait for first users-photo hydration (isInitialLoading). That flag is
  // first-shot-only and never reset on background refetch — prevents empty shell flash
  // without re-showing the full-screen loader on edits/refreshes.
  const shouldShowSkeleton = (
    (!displayUser && !authUser) ||
    (!cachedUser && !!authUser && retryAttempt < MAX_RETRIES && !needsOnboardingDbRow) ||
    (!!authUser && isInitialLoading)
  );

  const showStatsSkeleton = statsLoading && !statsEverLoadedRef.current;
  const showStatsStaleRefresh = statsLoading && statsEverLoadedRef.current;
  const trustBadgeDisplay = displayTrustScore;

  // V325.1: Compute resolved CTA mode for fixed CTA bar (outside scroll flow)
  const userDataForCta = freshUserData !== null ? freshUserData : cachedUser;
  const hasNameCta = !!userDataForCta?.name;
  const hasDOBCta = !!userDataForCta?.date_of_birth;
  const hasPhotoCta = !!userDataForCta?.photo_urls && userDataForCta.photo_urls.length > 0;
  const needsOnboardingProfile =
    !isVerifiedSubscription &&
    (freshUserData === null
      ? (!cachedUser?.name || !cachedUser?.date_of_birth)
      : (!hasNameCta || !hasDOBCta));
  const isCtaReady = profileDataLoaded && (
    !verificationLoading || cachedUser?.is_verified != null || entitlements != null
  );
  const resolvedCtaMode: 'onboarding' | 'upgrade' | 'verified' =
    isVerifiedSubscription ? 'verified'
      : needsOnboardingProfile ? 'onboarding'
        : 'upgrade';

  const showFoundingCard = Boolean(barIlanUserData && currentUser?.id);
  const showFoundingPlaceholder = Boolean(currentUser?.id && !barIlanFetchSettled);

  // P1-FLICKER: DEV-only transition diagnostics
  const profileMountMsRef = useRef(typeof performance !== 'undefined' ? performance.now() : 0);
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const elapsed = Math.round(performance.now() - profileMountMsRef.current);
    if (elapsed > 2000) return;
    console.log('[P1-FLICKER] transition', {
      elapsedMs: elapsed,
      shouldShowSkeleton,
      statsLoading,
      statsEverLoaded: statsEverLoadedRef.current,
      verificationLoading,
      isCtaReady,
      showFoundingCard,
      showFoundingPlaceholder,
      cachedUser: !!cachedUser,
    });
  }, [
    shouldShowSkeleton,
    statsLoading,
    verificationLoading,
    isCtaReady,
    showFoundingCard,
    showFoundingPlaceholder,
    cachedUser,
  ]);

  if (import.meta.env.DEV) {
    console.log('[V325.1 CTA DEBUG]', {
      resolvedCtaMode,
      isCtaReady,
      needsOnboardingProfile,
      isVerifiedSubscription,
      hasAuthUser: !!authUser?.id,
      hasCurrentUser: !!currentUser?.id,
    });
  }

  // Invert splash handoff: dismiss global HTML splash only when Profile is paint-ready
  useEffect(() => {
    if (shouldShowSkeleton || !profile) return;
    signalBootReady('profile-ready');
  }, [shouldShowSkeleton, profile]);

  if (shouldShowSkeleton || !profile) {
    if (import.meta.env.DEV && shouldShowSkeleton) {
      console.log('⏳ P1-FLICKER: Profile loading (no full-screen splash)', {
        hasAuthUser: !!authUser,
        hasCachedUser: !!cachedUser,
        retryAttempt,
        isInitialLoading,
        bootReady: isBootReady(),
      });
    }

    // Cold boot: global HTML splash covers the screen — render nothing conflicting
    if (!isBootReady()) {
      return <div className="w-full h-full min-h-0" aria-busy="true" />;
    }

    // In-app navigation: layout-matching skeleton (not a full-screen blackout)
    return <ProfilePageSkeleton />;
  }

  return (
    <div className="profile-page-container w-full bg-gradient-to-b from-[#FAF7F5] to-[#F3EDE8] dark:bg-gradient-to-br dark:from-blue-900 dark:via-purple-900 dark:to-indigo-900">
      {/* V252: Onboarding photo splash overlay */}
      {fromOnboardingPhoto && !photoReady && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-2xl max-w-sm mx-4">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-900 dark:text-white text-lg font-semibold">Refreshing profile...</p>
              <p className="text-gray-600 dark:text-gray-400 text-sm text-center">Your new photo is being saved</p>
            </div>
          </div>
        </div>
      )}
      <div className="profile-content-area pb-[88px]">
        <div className="max-w-sm sm:max-w-md md:max-w-3xl mx-auto space-y-3 md:space-y-0 md:py-4 px-3">
          <div className="backdrop-blur-xl bg-white/80 dark:bg-white/10 border border-[#E8E0DA]/50 dark:border-white/20 rounded-2xl sm:rounded-3xl p-3 sm:p-4 shadow-[0_2px_16px_rgba(120,80,60,0.06)] dark:shadow-2xl relative profile-card">
            {/* Mobile: Original layout with absolute header */}
            <div className="absolute top-2 sm:top-3 left-2 sm:left-3 right-2 sm:right-3 flex justify-between items-center z-10 md:hidden">
              {/* V291.2: Trust score badge with semi-transparent green styling */}
              <button
                onClick={() => setShowTrust(true)}
                className="bg-green-500/20 backdrop-blur-sm border border-green-500/30 rounded-full px-3 py-1.5 sm:py-2 min-w-[50px] hover:bg-green-500/30 transition-colors profile-trust-button profile-button shadow-lg"
                aria-label={`View trust score details. Current score: ${trustBadgeDisplay}`}
              >
                <div className="text-green-400 text-xs sm:text-sm font-semibold tabular-nums min-w-[2ch] text-center">
                  {trustBadgeDisplay}
                </div>
              </button>

              {/* V232.1: Android-safe custom dropdown anchored to 3-dots button (no portal, no top-left jump) */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsMenuOpen((open) => !open)}
                  className="text-[#6B6560] dark:text-white/60 hover:text-[#2D2A26] dark:hover:text-white p-1.5 sm:p-2 rounded-full hover:bg-[#F8F4F1] dark:hover:bg-white/10 profile-dropdown-trigger profile-button"
                  aria-label={t('nav.profile')}
                >
                  <MoreVertical className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                </button>

                {isMenuOpen && (
                  <>
                    {/* Backdrop to close menu when tapping outside */}
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsMenuOpen(false)}
                    />

                    {/* Menu content - positioned just below the trigger, aligned right */}
                    <div className="absolute end-0 top-full mt-1 z-50 rounded-lg bg-white dark:bg-slate-900 border border-[#E8E0DA] dark:border-slate-700 shadow-[0_4px_24px_rgba(120,80,60,0.10)]">
                      <button
                        type="button"
                        className="flex items-center whitespace-nowrap px-3 py-2 text-sm text-[#2D2A26] dark:text-gray-100 hover:bg-[#F8F4F1] dark:hover:bg-slate-800"
                        onClick={() => {
                          setIsMenuOpen(false);
                          setShowEdit(true);
                        }}
                      >
                        <Edit className="w-4 h-4 me-2 flex-shrink-0" aria-hidden="true" />
                        {t('discover.editProfile')}
                      </button>
                      <button
                        type="button"
                        className="flex items-center whitespace-nowrap px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                        onClick={() => {
                          setIsMenuOpen(false);
                          // Local-first logout: clear React session immediately, then hard-nav.
                          // Never await remote signOut before leaving authenticated UI.
                          void signOut();
                          clearLocalStoragePreservingCookieConsent();
                          try {
                            sessionStorage.clear();
                          } catch {
                            /* non-fatal */
                          }
                          window.location.href = '/';
                        }}
                      >
                        <LogOut className="w-4 h-4 me-2 flex-shrink-0" aria-hidden="true" />
                        {t('common.signOut')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Desktop: Top header - reduced height to fit viewport */}
            <div className="hidden md:flex justify-between items-center z-10">
              {/* V291.2: Trust score badge with semi-transparent green styling */}
              <button
                onClick={() => setShowTrust(true)}
                className="bg-green-500/20 backdrop-blur-sm border border-green-500/30 rounded-full px-3 py-1.5 sm:py-2 min-w-[50px] hover:bg-green-500/30 transition-colors profile-trust-button profile-button shadow-lg"
                aria-label={`View trust score details. Current score: ${trustBadgeDisplay}`}
              >
                <div className="text-green-400 text-xs sm:text-sm font-semibold tabular-nums min-w-[2ch] text-center">
                  {trustBadgeDisplay}
                </div>
              </button>

              {/* V232.1: Desktop menu uses same anchored dropdown implementation */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsMenuOpen((open) => !open)}
                  className="text-[#6B6560] dark:text-white/60 hover:text-[#2D2A26] dark:hover:text-white p-1.5 sm:p-2 rounded-full hover:bg-[#F8F4F1] dark:hover:bg-white/10 profile-dropdown-trigger profile-button"
                  aria-label={t('nav.profile')}
                >
                  <MoreVertical className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                </button>

                {isMenuOpen && (
                  <>
                    {/* Backdrop to close menu when clicking outside (desktop) */}
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsMenuOpen(false)}
                    />

                    <div className="absolute end-0 top-full mt-1 z-50 rounded-lg bg-white dark:bg-slate-900 border border-[#E8E0DA] dark:border-slate-700 shadow-[0_4px_24px_rgba(120,80,60,0.10)]">
                      <button
                        type="button"
                        className="flex items-center whitespace-nowrap px-3 py-2 text-sm text-[#2D2A26] dark:text-gray-100 hover:bg-[#F8F4F1] dark:hover:bg-slate-800"
                        onClick={() => {
                          setIsMenuOpen(false);
                          setShowEdit(true);
                        }}
                      >
                        <Edit className="w-4 h-4 me-2 flex-shrink-0" aria-hidden="true" />
                        {t('discover.editProfile')}
                      </button>
                      <button
                        type="button"
                        className="flex items-center whitespace-nowrap px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                        onClick={() => {
                          setIsMenuOpen(false);
                          void signOut();
                          clearLocalStoragePreservingCookieConsent();
                          try {
                            sessionStorage.clear();
                          } catch {
                            /* non-fatal */
                          }
                          window.location.href = '/';
                        }}
                      >
                        <LogOut className="w-4 h-4 me-2 flex-shrink-0" aria-hidden="true" />
                        {t('common.signOut')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Mobile: Original layout */}
            <div className="flex flex-col pt-6 pb-3 md:hidden">
              {/* Profile image container with mobile-first positioning */}
              <div className="flex justify-center items-center mb-4 w-full">
                {/* Mobile: smaller photo so CTA stays visible */}
                <div className="w-full max-w-[280px] sm:max-w-[340px]">
                  <div className="relative profile-photo-wrapper">
                    {/* Image positioned slightly down and right */}
                    {/* V214: CSS-only sizing - no inline styles to allow media queries to work */}
                    {/* V280: Aspect ratio container - enforces 4:5 ratio */}
                    <div
                      className="profile-hero-photo-container overflow-hidden rounded-xl relative block w-full aspect-[4/5]"
                    >
                      {/* V268.5: Debug photo URLs before rendering */}
                      <ProfileHeroPhoto
                        // V266 + V320: profile.photo_urls is already normalized (Cloudinary only, stable order)
                        userId={currentUser?.id || ''}
                        userName={profile.name}
                        photoUrls={profile.photo_urls}
                        onClick={() => {
                          // V150.27: Profile photo clicked - ALWAYS opening PhotoViewer modal
                          setShowPhoto(true);
                        }}
                        showUploadButton={false}
                      />
                    </div>

                    {/* Edit button positioned close to image frame - mobile first */}
                    {/* 🎯 V182.2: TASK 4 - Added aria-label for accessibility (WCAG AA) */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowEdit(true);
                      }}
                      className="absolute -bottom-2 -right-2 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white p-2.5 rounded-full shadow-lg transition-all hover:scale-105 active:scale-95 border-2 border-white profile-edit-button profile-button z-20 sm:p-3 sm:-bottom-3 sm:-right-3"
                      aria-label="Edit profile"
                    >
                      <Edit className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>

              {/* V206.19: Upload button removed - automatic Cloudinary processing on signup */}
              <h1 className="heading-lg text-[#2D2A26] dark:text-white text-center profile-title">
                {getDisplayNameWithOptionalAge(profile, profile.age)}
              </h1>
            </div>

            {/* V228: Desktop image section - flex-1 to fill all available space */}
            <div className="hidden md:flex flex-1 min-h-0 justify-center items-center w-full profile-image-section">
              {/* V280: Responsive max-width wrapper to control desktop image size */}
              <div className="w-full max-w-[320px] sm:max-w-[360px] md:max-w-[420px] lg:max-w-[480px]">
                <div className="relative profile-photo-wrapper">
                  {/* Image positioned slightly down and right */}
                  {/* V214: CSS-only sizing - no inline styles to allow media queries to work */}
                  {/* V280: Aspect ratio container - enforces 4:5 ratio */}
                  <div
                    className="profile-hero-photo-container overflow-hidden rounded-xl relative block w-full aspect-[4/5]"
                  >
                    <ProfileHeroPhoto
                      // V320: profile.photo_urls is normalized (Cloudinary only)
                      userId={currentUser?.id || ''}
                      userName={profile.name}
                      photoUrls={profile.photo_urls}
                      onClick={() => {
                        setShowPhoto(true);
                      }}
                      showUploadButton={false}
                    />
                  </div>

                  {/* Edit button positioned close to image frame - desktop: inside container */}
                  {/* 🎯 V182.2: TASK 4 - Added aria-label for accessibility (WCAG AA) */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowEdit(true);
                    }}
                    className="absolute bottom-2 right-2 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white p-2.5 rounded-full shadow-lg transition-all hover:scale-105 active:scale-95 border-2 border-white profile-edit-button profile-button z-20 md:p-3"
                    aria-label="Edit profile"
                  >
                    <Edit className="w-4 h-4 md:w-5 md:h-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>

            {/* V228: Desktop name footer - minimal height */}
            <div className="hidden md:flex flex-shrink-0 py-2 items-center justify-center">
              {/* V206.19: Upload button removed - automatic Cloudinary processing on signup */}
              <h1 className="heading-lg text-[#2D2A26] dark:text-white text-center profile-title">
                {getDisplayNameWithOptionalAge(profile, profile.age)}
              </h1>
            </div>

          </div>
          {/* V228: Stats card - visible on ALL viewports (mobile + desktop) */}
          <div className="backdrop-blur-xl bg-white/80 dark:bg-white/10 border border-[#E8E0DA]/50 dark:border-white/20 rounded-2xl sm:rounded-3xl p-3 sm:p-4 md:p-2 shadow-[0_2px_16px_rgba(120,80,60,0.06)] dark:shadow-2xl profile-card">
            {statsError && (
              <div className="network-stats-error text-red-600 dark:text-red-200 text-sm text-center">
                <p>⚠️ {statsError}</p>
              </div>
            )}

            {!statsError && (
              <NetworkStatsDisplay
                connectionsCount={networkStats.connectionsCount}
                matchesCount={networkStats.matchesCount}
                friendsCount={networkStats.friendsCount}
                secondDegreeCount={secondDegreeCount}
                loading={showStatsSkeleton}
                staleWhileRevalidate={showStatsStaleRefresh}
                onConnectionsClick={() => navigate('/network?tab=all')}
                onMatchesClick={() => navigate('/network?tab=favorites')}
                onFriendsClick={() => navigate('/network?tab=first')}
              />
            )}
          </div>

          {/* V322: Founding 100 Contest Card - reserved space prevents layout push */}
          {showFoundingPlaceholder && (
            <div className="backdrop-blur-xl bg-white/80 dark:bg-white/10 border-2 border-pink-300/50 dark:border-pink-500/30 rounded-2xl sm:rounded-3xl p-4 min-h-[128px] profile-card animate-pulse">
              <div className="h-full flex items-center justify-center">
                <div className="h-4 w-32 rounded bg-[#E8E0DA]/60 dark:bg-white/10" />
              </div>
            </div>
          )}
          {showFoundingCard && (
            <div ref={cohortSectionRef}>
              <FoundingContestCard
                userId={currentUser.id}
                userTrustScore={networkStats.trustScore}
                networkSize={networkStats.connectionsCount}
                bottomPadding={Number.isFinite(bottomPadding) ? bottomPadding : 88}
              />
            </div>
          )}

          {/* ─── V325.1: Inline CTA — fixed height, no branch height swap ─── */}
          <div className="w-full">
            <div className="backdrop-blur-xl bg-white/80 dark:bg-white/10 border border-[#E8E0DA]/50 dark:border-white/20 rounded-2xl sm:rounded-3xl p-3 shadow-[0_2px_16px_rgba(120,80,60,0.06)] min-h-[72px] flex items-center">
              {!isCtaReady ? (
                <Button className="w-full min-h-[48px]" size="lg" disabled>
                  {t('common.loading')}
                </Button>
              ) : resolvedCtaMode === 'onboarding' ? (
                <Button
                  className="w-full min-h-[48px] bg-gradient-to-r from-[#8B5CF6] via-[#A855F7] to-[#D946A8] text-white font-semibold rounded-2xl shadow-[0_4px_24px_rgba(139,92,246,0.30)] hover:shadow-[0_6px_32px_rgba(139,92,246,0.40)] hover:scale-[1.02] transition-all duration-200"
                  size="lg"
                  onClick={() => navigate('/onboarding?source=profile')}
                >
                  {t('profile.finishSetup')}
                </Button>
              ) : resolvedCtaMode === 'upgrade' ? (
                <Button
                  className="w-full min-h-[48px] bg-gradient-to-r from-[#8B5CF6] via-[#A855F7] to-[#D946A8] text-white font-semibold rounded-2xl shadow-[0_4px_24px_rgba(139,92,246,0.30)] hover:shadow-[0_6px_32px_rgba(139,92,246,0.40)] hover:scale-[1.02] transition-all duration-200"
                  size="lg"
                  onClick={() => {
                    if (!hasNameCta || !hasDOBCta || !hasPhotoCta) {
                      navigate('/onboarding?source=verify');
                      return;
                    }
                    setShowPaywall(true);
                  }}
                >
                  {t('profile.getVerified')}
                </Button>
              ) : (
                <Button
                  className="w-full min-h-[48px] bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold"
                  size="lg"
                  onClick={() => setShowVerifiedInfo(true)}
                >
                  {t('profile.verified')}
                </Button>
              )}
            </div>
          </div>
          {/* ─────────────────────────────────────────────────────────────── */}

        </div >
      </div >

      <Suspense fallback={null}><TrustScoreDialog isOpen={showTrust} onClose={() => setShowTrust(false)} trustScore={displayTrustScore} userName={profile.name} firstDegreeCount={canonicalFriendsCount} secondDegreeCount={canonicalSecondDegreeCount} isVerified={effectiveVerified} /></Suspense>
      <EditProfileModal isOpen={showEdit} onClose={() => setShowEdit(false)} profile={profile} onSave={handleSave} />
      <Suspense fallback={null}>
        {showPhoto && (() => {
          // V276.1: STEP 6 - Props being passed to ProfilePhotoViewer
          console.log('[V276.1] ════════════════════════════════════════');
          console.log('[V276.1] STEP 6: Rendering ProfilePhotoViewer with props');
          console.log('[V276.1] pendingCameraFile:', pendingCameraFile?.name || 'NULL');
          console.log('[V276.1] pendingCameraPreview exists:', !!pendingCameraPreview);
          console.log('[V276.1] pendingCameraPreview:', pendingCameraPreview?.substring(0, 50) || 'NULL');
          console.log('[V276.1] pendingCameraDataUrl exists:', !!pendingCameraDataUrl);
          console.log('[V276.1] pendingCameraDataUrl type:', typeof pendingCameraDataUrl);
          console.log('[V276.1] pendingCameraDataUrl is null:', pendingCameraDataUrl === null);
          console.log('[V276.1] pendingCameraDataUrl is undefined:', pendingCameraDataUrl === undefined);
          console.log('[V276.1] pendingCameraDataUrl is empty string:', pendingCameraDataUrl === '');
          console.log('[V276.1] pendingCameraDataUrl length:', pendingCameraDataUrl?.length || 0);
          console.log('[V276.1] pendingCameraDataUrl prefix:', pendingCameraDataUrl?.substring(0, 50) || 'NULL');
          console.log('[V276.1] initialFile prop:', (pendingCameraFile || undefined) ? 'SET' : 'UNDEFINED');
          console.log('[V276.1] initialPreviewUrl prop:', (pendingCameraPreview || undefined)?.substring(0, 50) || 'UNDEFINED');
          console.log('[V276.1] initialDataUrl prop value:', (pendingCameraDataUrl || undefined)?.substring(0, 50) || 'UNDEFINED');
          console.log('[V276.1] initialDataUrl prop type:', typeof (pendingCameraDataUrl || undefined));
          console.log('[V276.1] initialDataUrl prop is undefined:', (pendingCameraDataUrl || undefined) === undefined);
          console.log('[V276.1] ════════════════════════════════════════');

          return (
            <ProfilePhotoViewer
              // V266: Removed key prop - no forced remounting, component updates naturally when props change
              photoUrl={profile.photo_urls?.[0] || unifiedPhotoUrl || ''} // V320: profile.photo_urls is normalized
              userName={profile.name}
              onClose={() => {
                setShowPhoto(false);
                // Clear pending camera file when closing
                setPendingCameraFile(null);
                setPendingCameraPreview(null);
                setPendingCameraDataUrl(null); // V276.1: Clear dataUrl too
              }}
              onEdit={() => {
                // V150.14: OPTIMISTIC UI - Open camera modal instead of navigating
                setShowPhoto(false);
                setShowCamera(true);
              }}
              isOwnProfile={true}
              userId={currentUser?.id}
              initialFile={pendingCameraFile || undefined}
              initialPreviewUrl={pendingCameraPreview || undefined}
              initialDataUrl={pendingCameraDataUrl || undefined} // V276.1: Pass original dataUrl from camera
              onPhotoUpdate={handlePhotoUpdate}
            />
          );
        })()}
      </Suspense>

      {/* V150.14: OPTIMISTIC UI - Camera modal for instant photo capture */}
      {
        showCamera && (
          <div className="fixed inset-0 z-50">
            <ProfilePhotoCapture
              onComplete={handleCameraComplete}
              onSkip={() => setShowCamera(false)}
              onFileSelected={handleCameraFileSelected}
            />
          </div>
        )
      }

      {/* V145.4: NUCLEAR PHOTO RECOVERY SYSTEM */}
      <PhotoRecoveryModal
        isOpen={showPhotoRecovery}
        onClose={() => setShowPhotoRecovery(false)}
        onPhotoUploaded={handlePhotoRecoveryComplete}
      />

      {/* V281.4: Verify Paywall Dialog - Safe area padding + ThirdDegreeGate styling */}
      <Dialog open={showPaywall} onOpenChange={setShowPaywall}>
        <FullscreenDialogContent>
          <DialogTitle className="sr-only">Get Verified!</DialogTitle>
          <DialogDescription className="sr-only">
            Get verified and gain +100 trust score, Verified Badge, Priority Ranking, and Premium Customer Service for $9.99/month.
          </DialogDescription>
          {/* Outer flex: centers horizontally, stretches vertically */}
          <div className="flex h-full w-full items-stretch justify-center">
            {/* V281.4: Inner card with safe area padding */}
            <div
              className="
                h-full w-full
                max-w-[480px] md:max-w-[760px]
                overflow-hidden rounded-none md:rounded-3xl shadow-2xl
                bg-gradient-to-b from-slate-950 via-slate-900 to-[#050011]
                pt-4 pb-6
              "
            >
              <VerifyPaywall onClose={() => setShowPaywall(false)} />
            </div>
          </div>
        </FullscreenDialogContent>
      </Dialog>

      {/* V191.2: Thank-you dialog — localized VerifiedStatusDialog */}
      {showThankYou && (
        <VerifiedStatusDialog
          showRenewalNote={isVerifiedSubscription}
          onManage={() => {
            setShowThankYou(false);
            setSearchParams({});
            setShowProfileManageDialog(true);
          }}
          onDone={async () => {
            await refreshProfileAfterVerifiedDialog();
            setShowThankYou(false);
            setSearchParams({});
          }}
          onClose={async () => {
            await refreshProfileAfterVerifiedDialog();
            setShowThankYou(false);
            setSearchParams({});
          }}
        />
      )}

      {/* V196.5: Verified badge info — localized VerifiedStatusDialog */}
      {showVerifiedInfo && (
        <VerifiedStatusDialog
          showRenewalNote={isVerifiedSubscription}
          onManage={() => {
            setShowVerifiedInfo(false);
            setShowProfileManageDialog(true);
          }}
          onDone={() => setShowVerifiedInfo(false)}
          onClose={() => setShowVerifiedInfo(false)}
        />
      )}

      {/* Profile subscription management — localized ManageSubscriptionDialog */}
      {showProfileManageDialog && (
        <ManageSubscriptionDialog
          onOpenStoreSettings={() => { void openSubscriptionStoreSettings(); }}
          onContactSupport={() => { void contactSubscriptionSupport(); }}
          onClose={() => setShowProfileManageDialog(false)}
        />
      )}
    </div >
  );
};

export default React.memo(Profile);
